// src/lib/pdfExport.js
// PDF-Export fuer Wartungsprotokolle: Wartungsbericht fuer den Kunden,
// Vollprotokoll (alle Pruefpunkte) und Maengelliste.
// Erzeugt das PDF komplett im Browser mit pdfmake - funktioniert auch offline.
//
// Gestaltung seit v58 (15.09.2026, Anna-Lena: "schoen und professionell, trotzdem
// schnell und leicht erkennbar"):
//   - Kleine Tabellen werden NIE ueber zwei Seiten gerissen (zusammen()).
//   - Eine Ueberschrift steht nie allein unten auf der Seite (seitenumbruchVor).
//   - Ergebnis-Spalte zeigt nur i.O. / n.i.O. als Farbfeld; Zusaetze wie
//     "fehlt", "veraltet", Temperatur oder Bemerkung stehen unter "Hinweis".
//   - Seite 1 des Kundenberichts hat "Auf einen Blick": Zustand, Maengel,
//     Sicherheit, naechste Wartung.
import { LOGO_DATA_URL } from './logo';
import { istChunkFehler, nachUpdateNeuLaden } from './fehlerprotokoll';
import { handlungsempfehlung, ergebnisUndHinweis, istSichtbar, anzeigeNummern } from './pruefpunktLogik';
import { passendeBroschueren, lebensdauerBefund, LEBENSDAUER_JAHRE } from './broschueren';

import { supabase } from './supabase';
import { MS_FELD_TEXT, MS_FELD_KURZ, msFeldBezeichnung } from './auswahllisten';
import { anlagenLage, kopfFragenLage } from './anlagenRegeln';

// ---------------------------------------------------------------------------
// Marken-Farben
// ---------------------------------------------------------------------------
const NAVY = '#0A1628';
const ANTHRAZIT = '#454C59';   // weicher Anthrazit fuer Tabellenkoepfe
const GREEN = '#1A7A4A';
const GREENBG = '#E9F6EF';
const RED = '#C0392B';
const REDBG = '#FCEDEA';
const ORANGE = '#D97706';
const ORANGEBG = '#FEF3E2';   // heller Hintergrund fuer die orange Stufe (v90)
const GRAY = '#6B7A95';
const GRAYDARK = '#3A4254';
const GRAYLINE = '#D4DCE8';
const GRAYBG = '#F1F4F8';
const BRANDGREEN = '#8CC63F';
const SEITENBREITE = 511;      // A4 minus Raender (42 + 42)

// Firmen-Logo (eingebettet, offline-faehig) aus lib/logo.js.

// ---------------------------------------------------------------------------
// Wert-Darstellung
// ---------------------------------------------------------------------------

function istBeantwortet(pp) {
  return pp.wert_auswahl != null || pp.wert_zahl != null ||
    pp.wert_bool != null || (pp.wert_text && pp.wert_text.length > 0);
}

// ---------------------------------------------------------------------------
// Helfer
// ---------------------------------------------------------------------------
function fmt(v) {
  if (v == null || v === '') return '–';
  if (v === true) return 'ja';
  if (v === false) return 'nein';
  return String(v);
}
function jaNein(v) { return v ? 'ja' : 'nein'; }
function hatWert(v) { return v != null && v !== ''; }
function anschrift(strasse, plz, ort) {
  return [strasse, [plz, ort].filter(hatWert).join(' ')].filter(s => s && s.trim()).join(', ');
}
function fmtDatum(d) {
  if (!d) return '';
  const m = String(d).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return m[3] + '.' + m[2] + '.' + m[1];
  return String(d);
}
// Spannung fuer die Anzeige in kV. Der Import hat Volt gespeichert (20000 / 400),
// die App speichert kV (20 / 0,4) - frueher stand deshalb "20000 kV" im PDF.
// Werte ab 100 sind Volt und werden umgerechnet. Komma-Texte wie '0,4' gehen auch.
function fmtSpannungKv(v) {
  if (!hatWert(v)) return null;
  let n = typeof v === 'number' ? v : Number(String(v).trim().replace(',', '.'));
  if (!isFinite(n)) return String(v);
  if (n >= 100) n = n / 1000;
  return n.toLocaleString('de-DE', { maximumFractionDigits: 3, useGrouping: false }) + ' kV';
}
// Wartungszyklus lesbar machen: in der Datenbank stehen Codes wie '3-jaehrig'
// (Formular), '3-jährig' (Import) oder 'kein_zyklus_bekannt'.
function wartungszyklusText(z) {
  if (!hatWert(z)) return null;
  if (z === 'kein_zyklus_bekannt') return 'kein Zyklus bekannt';
  return String(z).replace(/-j(ae|ä)hrig/g, '-jährig').replace(/_/g, ' ');
}
const zahlDe = (v, einheit) => (hatWert(v) ? String(v).replace('.', ',') + (einheit ? ' ' + einheit : '') : null);

const TRAFOART_LABEL = {
  hermetic: 'Hermetik (Öl, geschlossen)',
  giessharz: 'Gießharz (trocken)',
  oel_mit_ausdehner: 'Öl mit Ausdehner',
};
const MS_FELD_LABEL = MS_FELD_TEXT;
const GESAMTZUSTAND_TEXT = {
  '++': 'sehr gut', '+': 'gut', '0': 'befriedigend', '-': 'mangelhaft', '--': 'ungenügend',
};
const ZUSTAND_FARBE = { '++': GREEN, '+': '#5BA476', '0': GRAY, '-': ORANGE, '--': RED };

// ---------------------------------------------------------------------------
// Bausteine
// ---------------------------------------------------------------------------
function logoBlock(breite = 78) {
  if (LOGO_DATA_URL && LOGO_DATA_URL.indexOf('data:image') === 0) {
    return { image: LOGO_DATA_URL, width: breite, alignment: 'right' };
  }
  return {
    alignment: 'right',
    stack: [
      { text: 'ENERGIE TECHNIK', color: GRAY, bold: true, fontSize: 13 },
      { text: 'BECKER', color: BRANDGREEN, bold: true, fontSize: 17 },
      { text: 'GmbH', color: GRAY, fontSize: 9 },
    ],
  };
}

// Kopf der ersten Seite: Dokumentart klein, Station gross, Kunde und Datum.
function titelKopf(dokumentart, titel, zeilen) {
  return [
    {
      columns: [
        { width: '*', stack: [
          { text: dokumentart.toUpperCase(), fontSize: 9, bold: true, color: GREEN, characterSpacing: 1.2 },
          { text: titel || '–', fontSize: 22, bold: true, color: NAVY, margin: [0, 3, 0, 3] },
          ...zeilen.filter(Boolean).map(z => ({ text: z, fontSize: 10, color: GRAYDARK, margin: [0, 1, 0, 0] })),
        ] },
        { width: 'auto', stack: [logoBlock(92)] },
      ],
      margin: [0, 0, 0, 10],
    },
    { canvas: [{ type: 'rect', x: 0, y: 0, w: SEITENBREITE, h: 3, color: BRANDGREEN }], margin: [0, 0, 0, 14] },
  ];
}

// Hält Elemente auf einer Seite zusammen (kleine Tabellen samt Ueberschrift).
// Ueberschriften darin verlieren ihre Umbruch-Regel: Der Block wandert als
// Ganzes, ein Umbruch mitten im Block zerlegte ihn sonst doch.
//
// ACHTUNG (Pruefung 17.09.2026): pdfmake laesst einen unbreakable-Block, der
// hoeher als eine Seite ist, KOMPLETT weg - ohne Fehler. So fehlten z. B. die
// Messungen bei drei Erdungsschienen oder die Bewertung bei einer langen
// Bemerkung. Deshalb wird die Hoehe grob geschaetzt; ist der Block zu gross,
// darf er normal umbrechen.
const ZUSAMMEN_MAX_HOEHE = 520;   // pt - Seite hat ca. 740 pt Satzspiegel
// zeichen: wie viele Zeichen ungefaehr in eine Zeile passen (Tabellenzellen und
// Spalten sind schmaler - lieber zu hoch schaetzen als zu niedrig).
function geschaetzteHoehe(k, zeichen = 95) {
  if (k == null || k === '') return 0;
  if (typeof k === 'string') return Math.max(1, Math.ceil(k.length / zeichen)) * 12;
  if (Array.isArray(k)) return k.reduce((s, x) => s + geschaetzteHoehe(x, zeichen), 0);
  const rand = Array.isArray(k.margin) ? (k.margin[1] || 0) + (k.margin[3] || 0) : 0;
  if (k.svg) {
    const m = String(k.svg).match(/height="(\d+(?:\.\d+)?)"/);
    return (m ? Number(m[1]) : 150) + rand;
  }
  if (k.image) return (k.height || k.fit?.[1] || 200) + rand;
  if (k.stack) return geschaetzteHoehe(k.stack, zeichen) + rand;
  if (k.columns) return Math.max(0, ...k.columns.map(c => geschaetzteHoehe(c, 45))) + rand;
  if (k.ul || k.ol) return geschaetzteHoehe(k.ul || k.ol, zeichen) + rand;
  if (k.table && Array.isArray(k.table.body)) {
    return k.table.body.reduce((s, zeile) =>
      s + Math.max(14, ...(Array.isArray(zeile) ? zeile : []).map(z => geschaetzteHoehe(z, 40) + 6)), 0) + rand;
  }
  if (k.text != null) {
    const t = Array.isArray(k.text) ? k.text.map(x => (typeof x === 'string' ? x : x?.text || '')).join('') : String(k.text);
    const zeilen = t.split('\n').reduce((s, z) => s + Math.max(1, Math.ceil(z.length / zeichen)), 0);
    return zeilen * ((k.fontSize || 9) * 1.35) + rand;
  }
  return 14 + rand;
}
function zusammen(...teile) {
  const liste = teile.flat().filter(Boolean);
  if (geschaetzteHoehe(liste) > ZUSAMMEN_MAX_HOEHE) return { stack: liste };
  const ohneRegel = (t) => (t && t.headlineLevel ? { ...t, headlineLevel: undefined } : t);
  return { stack: liste.map(ohneRegel), unbreakable: true };
}

// Abschnittsueberschrift mit gruener Nummer.
function abschnitt(nr, titel) {
  return {
    headlineLevel: 1,
    table: { widths: [22, '*'], body: [[
      { text: String(nr), bold: true, fontSize: 11, color: '#FFFFFF', fillColor: GREEN, alignment: 'center' },
      { text: titel, bold: true, fontSize: 12, color: NAVY, fillColor: GRAYBG },
    ]] },
    layout: { hLineWidth: () => 0, vLineWidth: () => 0,
      paddingTop: () => 5, paddingBottom: () => 5, paddingLeft: (i) => (i === 0 ? 0 : 9), paddingRight: () => 6 },
    margin: [0, 16, 0, 8],
  };
}
function subTitel(text) {
  return { text, headlineLevel: 2, bold: true, fontSize: 10, color: NAVY, margin: [0, 8, 0, 4] };
}
function hinweisZeile(text) { return { text, style: 'leer', margin: [0, 0, 0, 6] }; }

// Texte fuer den Kunden (Anna-Lena 21.09.2026): Mit "Pos. 05.2" und "Auto-Bewertung"
// kann der Kunde nichts anfangen - es zaehlt nur die Aussage. In der Datenbank
// bleibt "Auto-Bewertung:" stehen, daran erkennt die App ihren eigenen Text.
function kundenText(s) {
  return String(s ?? '')
    .replace(/Auto-Bewertung:\s*/gi, '')
    .replace(/n\.?\s?i\.?\s?O\.?\s+an\s+Pos\.\s*[\d.?]+/gi, 'nicht in Ordnung')
    .trim();
}
// Pruefpunkt-Texte ohne Doppelpunkt am Ende ("Isolierstoffe:" -> "Isolierstoffe").
// Auch Klammerzusaetze wie "(nicht zulaessig - wenn ja: Mangel)" fallen weg - das ist
// ein Hinweis fuer den Monteur, nicht fuer den Kunden (Anna-Lena 21.09.2026).
const ohneDoppelpunkt = (s) => String(s ?? '')
  .replace(/\s*\([^)]*(zul(ä|ae)ssig|Mangel)[^)]*\)/gi, '')
  .replace(/\s*:\s*$/, '');

// Linien nur zwischen den Zeilen - ruhiger als ein volles Gitter.
const LAYOUT_RUHIG = {
  hLineWidth: (i, node) => (i === 0 || i === node.table.body.length ? 0.8 : 0.5),
  vLineWidth: () => 0,
  hLineColor: (i, node) => (i === 0 || i === node.table.body.length ? GRAYLINE : '#E6EBF2'),
  paddingTop: () => 4, paddingBottom: () => 4, paddingLeft: () => 6, paddingRight: () => 6,
};

// Label/Wert-Paare in zwei Spalten.
function kvTable(pairs) {
  const rows = pairs.filter(p => p && hatWert(p[1]));
  if (rows.length === 0) return null;
  const body = [];
  for (let i = 0; i < rows.length; i += 2) {
    const a = rows[i], b = rows[i + 1];
    body.push([
      { text: a[0], style: 'kLabel' }, { text: fmt(a[1]), style: 'kWert' },
      { text: b ? b[0] : '', style: 'kLabel' }, { text: b ? fmt(b[1]) : '', style: 'kWert' },
    ]);
  }
  return {
    table: { widths: [92, '*', 92, '*'], body, dontBreakRows: true },
    layout: LAYOUT_RUHIG,
    margin: [0, 0, 0, 8],
  };
}

// Tabellenkopf in Anthrazit, Zeilen nie zerrissen, Kopf auf Folgeseiten wiederholt.
function kopfZelle(text, extra) { return { text, style: 'thZelle', ...(extra || {}) }; }
function datenTabelle(widths, kopf, zeilen, extra) {
  return {
    table: { headerRows: 1, dontBreakRows: true, keepWithHeaderRows: 1, widths, body: [kopf, ...zeilen] },
    layout: {
      fillColor: (r) => (r === 0 ? ANTHRAZIT : null),
      hLineWidth: (i, node) => (i === 0 || i === 1 || i === node.table.body.length ? 0 : 0.5),
      vLineWidth: () => 0,
      hLineColor: () => '#E6EBF2',
      paddingTop: () => 4, paddingBottom: () => 4, paddingLeft: () => 6, paddingRight: () => 6,
    },
    margin: [0, 0, 0, 10],
    ...(extra || {}),
  };
}

// Farbfeld fuer das Ergebnis: hellgruen i.O., rot n.i.O., sonst schlicht.
function ergebnisZelle(text, inOrdnung, extra) {
  // i.O. dezent (hellgruen), n.i.O. kraeftig rot - so springen die Probleme ins Auge.
  if (inOrdnung === true) return { text: text || 'i.O.', style: 'pill', fillColor: GREENBG, color: GREEN, ...(extra || {}) };
  if (inOrdnung === false) return { text: text || 'n.i.O.', style: 'pill', fillColor: RED, color: '#FFFFFF', ...(extra || {}) };
  return { text: text || '–', style: 'tdZelle', alignment: 'center', color: text ? GRAYDARK : GRAY, ...(extra || {}) };
}

// Pruefpunkt-Tabelle: Pos | Pruefpunkt | Ergebnis | Hinweis, nach Kategorie gegliedert.
function pruefpunktTabelle(pps) {
  if (!pps || pps.length === 0) return null;
  const zeilen = [];
  let kategorie = null;
  pps.forEach((pp) => {
    if (pp.kategorie && pp.kategorie !== kategorie) {
      kategorie = pp.kategorie;
      zeilen.push([{ text: kategorie, colSpan: 4, bold: true, fontSize: 8.5, color: GREEN, fillColor: GRAYBG, margin: [0, 1, 0, 0] }, {}, {}, {}]);
    }
    const { ergebnis, hinweis } = ergebnisUndHinweis(pp);
    const nio = pp.in_ordnung === false;
    // Regel von Anna-Lena: In der schmalen Ergebnis-Spalte steht nur i.O./n.i.O.
    // Eine neutrale Antwort (z. B. "Fertigstation mit Anstrich") gehoert in den
    // Hinweis - sonst bricht sie die 48-pt-Spalte auf vier Zeilen um.
    const neutral = pp.in_ordnung == null && istBeantwortet(pp) && ergebnis;
    const hinweisText = neutral ? [ergebnis, hinweis].filter(Boolean).join(' · ') : hinweis;
    zeilen.push([
      { text: pp.anzeige_nummer || pp.pos_nummer || '', style: 'tdZelleKlein', fillColor: nio ? REDBG : null },
      { text: ohneDoppelpunkt(pp.bezeichnung), style: 'tdZelle', fillColor: nio ? REDBG : null, bold: nio },
      ergebnisZelle(neutral || !istBeantwortet(pp) ? '' : ergebnis, pp.in_ordnung),
      { text: kundenText(hinweisText), style: 'tdZelleKlein', fillColor: nio ? REDBG : null, color: nio ? RED : GRAYDARK },
    ]);
  });
  return datenTabelle([34, '*', 48, 150],
    [kopfZelle('Pos.'), kopfZelle('Prüfpunkt'), kopfZelle('Ergebnis', { alignment: 'center' }), kopfZelle('Hinweis')],
    zeilen);
}

// Module nach Code finden
function modByCode(module, code) { return (module || []).filter(m => (m.modul_vorlagen?.code) === code); }

// Pruefpunkte eines Moduls (mit optionalem Untertitel) an content anhaengen.
// gedruckt (Set der Modul-IDs): jedes Modul erscheint hoechstens einmal, und am
// Ende laesst sich pruefen, ob ein Modul mit Pruefpunkten noch fehlt.
function pushModulPruefpunkte(content, mods, fallbackText, gedruckt) {
  let hatPP = false;
  (mods || []).forEach((m) => {
    if (gedruckt) {
      if (gedruckt.has(m.id)) return;
      gedruckt.add(m.id);
    }
    const t = pruefpunktTabelle(m.pruefpunkte);
    if (t) {
      const n = (m.pruefpunkte || []).filter(p => p.in_ordnung === false).length;
      content.push(subTitel('Prüfpunkte' + (m.bezugs_label ? ' – ' + m.bezugs_label : '')
        + (n > 0 ? '  (' + n + ' n.i.O.)' : '')));
      content.push(t);
      hatPP = true;
    }
  });
  if (!hatPP && fallbackText) content.push(hinweisZeile(fallbackText));
  return hatPP;
}

// Unterschriftsblock (bleibt immer zusammen).
function unterschriftBlock(protokoll) {
  const p = protokoll || {};
  const unten = [false, false, false, true];
  const klein = (t) => ({ text: t, fontSize: 7.5, color: GRAY, border: [false, false, false, false] });
  // Jede Zelle ein eigenes Objekt - pdfmake veraendert Knoten beim Setzen,
  // ein doppelt verwendetes Objekt wurde falsch gezeichnet.
  return zusammen(
    { text: 'Bestätigung', bold: true, fontSize: 10, color: NAVY, margin: [0, 16, 0, 2] },
    { text: 'Die Wartung wurde wie beschrieben durchgeführt.', style: 'kLabel', margin: [0, 0, 0, 4] },
    {
      table: { widths: [215, 50, 215], body: [
        [
          { text: [p.monteur_unterschrift_ort, fmtDatum(p.monteur_unterschrift_datum)].filter(hatWert).join(', ') || ' ',
            style: 'kWert', margin: [0, 34, 0, 1], border: unten, borderColor: [null, null, null, GRAY] },
          { text: '', border: [false, false, false, false] },
          p.monteur_unterschrift_data
            ? { image: p.monteur_unterschrift_data, fit: [180, 46], border: unten, borderColor: [null, null, null, GRAY] }
            : { text: ' ', margin: [0, 34, 0, 1], border: unten, borderColor: [null, null, null, GRAY] },
        ],
        [klein('Ort, Datum'), klein(''),
         klein('Unterschrift Monteur' + (p.monteur_unterschrift_name ? ' (' + p.monteur_unterschrift_name + ')' : ''))],
      ] },
      layout: { hLineWidth: () => 0.7, vLineWidth: () => 0, paddingLeft: () => 0, paddingRight: () => 0,
        paddingTop: () => 2, paddingBottom: () => 2 },
    },
  );
}

// Kopf- und Fusszeile, Seitenumbruch-Regeln, Schriften.
// Sichtpruefung (Migration 47): gleiche Berichte, andere Ueberschriften.
const istSicht = (p) => p?.art === 'sichtpruefung';
const artWort = (p) => (istSicht(p) ? 'Sichtprüfung' : 'Wartung');
const artProtokoll = (p) => (istSicht(p) ? 'Sichtprüfungsprotokoll' : 'Wartungsprotokoll');

function basisDocDef(content, docTyp, protokoll, station) {
  const stName = station?.bezeichnung || protokoll?.stationsbezeich || '';
  const kopfText = [docTyp, stName, fmtDatum(protokoll?.wartungs_datum)].filter(hatWert).join('  ·  ');
  return {
    pageSize: 'A4', pageMargins: [42, 54, 42, 50], content,
    info: { title: kopfText, author: 'EnergieTechnik Becker GmbH' },
    defaultStyle: { font: 'Roboto', fontSize: 9, color: NAVY, lineHeight: 1.15 },
    styles: {
      thZelle: { fontSize: 8.5, bold: true, color: '#FFFFFF' },
      tdZelle: { fontSize: 8.5, color: NAVY },
      tdZelleKlein: { fontSize: 8, color: GRAYDARK },
      pill: { fontSize: 8, bold: true, alignment: 'center' },
      kLabel: { fontSize: 8, color: GRAY },
      kWert: { fontSize: 9, color: NAVY },
      leer: { fontSize: 9, italics: true, color: GRAY },
      hinweis: { fontSize: 8.5, color: GRAYDARK, italics: true },
    },
    // Ab Seite 2 eine schmale Kopfzeile, damit jede Seite zuzuordnen ist.
    header: (seite) => (seite === 1 ? null : {
      margin: [42, 22, 42, 0],
      stack: [
        { columns: [
          { text: kopfText, fontSize: 8, color: GRAY },
          { text: 'EnergieTechnik Becker GmbH', fontSize: 8, color: GRAY, alignment: 'right' },
        ] },
        { canvas: [{ type: 'line', x1: 0, y1: 4, x2: SEITENBREITE, y2: 4, lineWidth: 0.8, lineColor: BRANDGREEN }] },
      ],
    }),
    footer: (seite, seiten) => ({
      margin: [42, 14, 42, 0],
      columns: [
        { text: 'EnergieTechnik Becker GmbH · Eugen-Langen-Straße 4 · 76227 Karlsruhe · Tel. 0721 / 968 68 58-0 · www.etb-karlsruhe.de',
          fontSize: 7, color: GRAY },
        { text: 'Seite ' + seite + ' von ' + seiten, fontSize: 7.5, color: GRAY, alignment: 'right', width: 70 },
      ],
    }),
    // Ueberschrift nie allein am Seitenende: Folgt auf derselben Seite kaum
    // noch etwas (weniger als ~2 Tabellenzeilen), beginnt sie auf der naechsten.
    // Zusaetzlich (21.09.2026): Steht die Ueberschrift im unteren Fuenftel der Seite,
    // passt vom Inhalt darunter kaum etwas - dann gleich auf die neue Seite.
    pageBreakBefore: (knoten, folgendeAufSeite) => {
      if (knoten.headlineLevel !== 1 && knoten.headlineLevel !== 2) return false;
      if (folgendeAufSeite.length < 12) return true;
      const lage = knoten.startPosition && knoten.startPosition.verticalRatio;
      return typeof lage === 'number' && lage > (knoten.headlineLevel === 1 ? 0.78 : 0.85);
    },
  };
}

// ---------------------------------------------------------------------------
// Vollprotokoll (alle Pruefpunkte)
// ---------------------------------------------------------------------------
export function buildVollprotokollDocDef(daten) {
  const { protokoll, kunde, vnb, station, config, msFelder, trafos, trafosAlle, wandler, module, maengel, erder } = daten;
  const content = [];
  const k = kunde || {};
  const s = station || {};
  const c = config || {};
  // Welche Module schon im PDF stehen - damit keins doppelt und keins gar nicht erscheint.
  const gedruckt = new Set();
  let nr = 0;

  const stName = s.bezeichnung || protokoll?.stationsbezeich || '';
  content.push(...titelKopf(artProtokoll(protokoll) + ' – alle Prüfpunkte', stName, [
    k.name, artWort(protokoll) + ' am ' + fmtDatum(protokoll?.wartungs_datum),
  ]));

  // Auftraggeber, Objekt, Energieversorger
  content.push(zusammen(abschnitt(++nr, 'Auftraggeber und Objekt'), kvTable([
    ['Kundennummer', k.kunden_nr], ['Name / Firma', k.name],
    ['Anschrift', anschrift(k.strasse, k.plz, k.ort)],
    ['Ansprechpartner', [k.ansprechpartner, k.position].filter(hatWert).join(', ')],
    ['Telefon', k.telefon], ['Mobil', k.mobil], ['E-Mail', k.email],
    ['Wartungsdatum', fmtDatum(protokoll?.wartungs_datum)],
    ['Durchgeführt von', protokoll?.monteure || protokoll?.monteur_unterschrift_name],
  ])));
  const hatObjekt = s.objekt_abweichend || ['objekt_name', 'objekt_strasse', 'objekt_ort', 'objekt_ansprechpartner', 'objekt_telefon', 'objekt_email'].some(f => hatWert(s[f]));
  if (hatObjekt) {
    content.push(zusammen(subTitel('Objekt'), kvTable([
      ['Bezeichnung', s.objekt_name],
      ['Anschrift', anschrift(s.objekt_strasse, s.objekt_plz, s.objekt_ort)],
      ['Ansprechpartner', [s.objekt_ansprechpartner, s.objekt_position].filter(hatWert).join(', ')],
      ['Telefon', [s.objekt_telefon, s.objekt_telefon_durchwahl].filter(hatWert).join(' / ')],
      ['Mobil', s.objekt_mobil], ['E-Mail', s.objekt_email],
    ])));
  }
  content.push(zusammen(subTitel('Energieversorger (VNB)'), vnb ? kvTable([
    ['Name', vnb.name],
    ['Anschrift', anschrift(vnb.strasse, vnb.plz, vnb.ort)],
    ['Ansprechpartner', vnb.ansprechpartner],
    ['Telefon', vnb.telefon],
    ['E-Mail', vnb.ansprechpartner_email || vnb.email],
    // Das Stationsformular schreibt den VNB-Namen in stations_nr, aeltere Importe in vnb_stationsbezeichnung.
    ['Stationsbez. beim VNB', s.vnb_stationsbezeichnung || s.stations_nr],
  ]) : hinweisZeile('Kein Energieversorger hinterlegt.')));

  // Station
  content.push(zusammen(abschnitt(++nr, 'Station'), kvTable([
    ['Bezeichnung', s.bezeichnung], ['Stations-Nr.', s.stations_nr],
    ['Standort', anschrift(s.standort_strasse, s.standort_plz, s.standort_ort)],
    ['Bauform', STATIONSTYP_TEXT[c.stations_typ] || c.stations_typ],
    ['Hersteller (Gebäude)', c.stations_hersteller], ['Modelltyp', c.stations_modelltyp],
    ['Seriennummer', c.stations_seriennummer], ['Baujahr', c.stations_baujahr || s.baujahr],
  ]),
  // Sichtpruefungsprotokoll: ohne Ausstattungs-Block (Anna-Lena 19.09.2026).
  // Ist nichts vorhanden, faellt auch die Ueberschrift weg.
  (() => {
    const tab = istSicht(protokoll) ? null : ausstattungTabelle(c, wandler);
    return tab ? [subTitel('Ausstattung'), tab] : null;
  })(),
  // Die Kopfzeilen des Papierprotokolls stehen seit 20.09.2026 auch hier - das
  // Vollprotokoll geht als ausfuehrliche Fassung an den Kunden.
  subTitel('Angaben zur Wartung'), angabenZurWartung(protokoll, c, msFelder, trafos)));
  pushModulPruefpunkte(content, modByCode(module, '01-STATIONSKOERPER'), null, gedruckt);

  // MS-Anlage
  const msTeile = [abschnitt(++nr, 'Mittelspannungsanlage')];
  if (c.ms_anlage_vorhanden) {
    msTeile.push(kvTable([
      ['Hersteller', c.ms_anlage_hersteller], ['Anzahl Felder', c.ms_anlage_anzahl_felder],
      ['Betriebsspannung', fmtSpannungKv(c.ms_anlage_betriebsspannung_kv)],
      ['Isolierart', ISOLIERART_TEXT[c.ms_anlage_isolierart] || c.ms_anlage_isolierart],
    ]));
    if (Array.isArray(msFelder) && msFelder.length > 0) {
      msTeile.push(subTitel('MS-Felder'));
      msTeile.push(datenTabelle([28, '*', 120, '*'],
        [kopfZelle('Feld'), kopfZelle('Bezeichnung'), kopfZelle('Typ'), kopfZelle('Hersteller')],
        msFelder.map((f, i) => [
          { text: String(i + 1), style: 'tdZelle' }, { text: msFeldBezeichnung(f), style: 'tdZelle' },
          { text: MS_FELD_LABEL[f.typ_code] || f.typ_code || '–', style: 'tdZelle' }, { text: f.hersteller || '–', style: 'tdZelle' }])));
      // UMZ-/Schutzgeraete der Leistungsschalterfelder (Migration 58) - nur Felder mit Angaben.
      const mitSchutz = msFelder.map((f, i) => ({ f, i })).filter(({ f }) => f.schutzgeraet
        && Object.values(f.schutzgeraet).some(v => v != null && v !== ''));
      if (mitSchutz.length > 0) {
        const w = (v) => (v == null || v === '' ? '–' : String(v));
        // Strom / Zeit als ein Wert; ist beides leer, nur ein Strich.
        const paar = (z, i, t) => (z[i] || z[t] ? w(z[i]) + ' A / ' + w(z[t]) + ' s' : '–');
        msTeile.push(subTitel('UMZ- / Schutzgeräte'));
        msTeile.push(datenTabelle([24, '*', 96, 64, 64, 64, '*'],
          [kopfZelle('Feld'), kopfZelle('Gerät'), kopfZelle('Seriennr. / Wandler'), kopfZelle('I> / t>'),
            kopfZelle('I>> / t>>'), kopfZelle('IE> / tE>'), kopfZelle('Bemerkung')],
          mitSchutz.map(({ f, i }) => {
            const z = f.schutzgeraet;
            return [
              { text: String(i + 1), style: 'tdZelle' },
              { text: [[z.hersteller, z.typ].filter(Boolean).join(' ') || '–',
                z.in_schaltfeld ? 'In Feld ' + z.in_schaltfeld + ' A' : '',
                z.relais_nennstrom ? 'Relais ' + z.relais_nennstrom + ' A' : ''].filter(Boolean).join('\n'), style: 'tdZelle' },
              // Wandler in einer Zeile: Uebersetzung · Leistung · Klasse
              { text: [w(z.seriennummer),
                [z.wandler, z.wandler_leistung_va ? z.wandler_leistung_va + ' VA' : '', z.wandler_klasse]
                  .filter(Boolean).join(' · ')].filter(Boolean).join('\n'), style: 'tdZelle' },
              { text: paar(z, 'i_gt', 't_gt'), style: 'tdZelle' },
              { text: paar(z, 'i_gtgt', 't_gtgt'), style: 'tdZelle' },
              { text: paar(z, 'ie_gt', 'te_gt'), style: 'tdZelle' },
              { text: w(z.notiz), style: 'tdZelle' },
            ];
          })));
      }
    }
  } else {
    msTeile.push(hinweisZeile('Keine MS-Anlage vorhanden.'));
  }
  content.push(zusammen(msTeile));
  // Pruefpunkte immer drucken, wenn das Modul welche hat - auch wenn die
  // Konfiguration inzwischen "keine MS-Anlage" sagt. Frueher fehlten sie dann.
  pushModulPruefpunkte(content, modByCode(module, '02-MS-ANLAGE'), null, gedruckt);

  // Transformatoren
  // Ueberschrift wandert mit dem ersten Trafo-Block (nie allein am Seitenende).
  let trafoKopf = abschnitt(++nr, 'Transformatoren');
  const trafoModule = modByCode(module, '04-TRAFO-SICHT');
  // Nachschlagen per ID auch fuer inzwischen deaktivierte Trafos (trafosAlle).
  const trafoNachId = {};
  (trafosAlle || trafos || []).forEach((t) => { trafoNachId[t.id] = t; });
  const trafoBlock = (t, titel, meins) => {
    const teile = [trafoKopf, subTitel(titel)];
    trafoKopf = null;
    if (t) teile.push(kvTable(trafoPaare(t, true)), schutzTabelle(t));
    content.push(zusammen(teile));
    pushModulPruefpunkte(content, meins, null, gedruckt);
  };
  const aktiveTrafos = Array.isArray(trafos) ? trafos : [];
  aktiveTrafos.forEach((t) => {
    trafoBlock(t, t.bezeichnung || 'Trafo', trafoModule.filter(m => m.bezugs_id === t.id));
  });
  // Trafo-Module, deren Trafo inzwischen deaktiviert oder geloescht ist: die
  // Pruefpunkte gehoeren trotzdem zu dieser Wartung und muessen ins PDF.
  const verwaisteTrafoModule = trafoModule.filter(m => !gedruckt.has(m.id) && (m.pruefpunkte || []).length > 0);
  verwaisteTrafoModule.forEach((m) => {
    const t = trafoNachId[m.bezugs_id] || null;
    trafoBlock(t, (t && t.bezeichnung) || m.bezugs_label || m.modul_vorlagen?.name || 'Trafo', [m]);
  });
  if (aktiveTrafos.length === 0 && verwaisteTrafoModule.length === 0) {
    content.push(zusammen(trafoKopf, hinweisZeile('Keine Transformatoren erfasst.')));
  }
  // Oel-Auffangwanne - seit Migration 70 je Trafo ein Kapitel (Untertitel mit dem
  // Trafo-Namen aus bezugs_label), alte Protokolle stationsweit. Kapitel eines
  // Giessharz-Trafos haben keine sichtbaren Pruefpunkte und fallen weg.
  const oelMod = modByCode(module, '05-OEL-WANNE');
  if (oelMod.some(m => (m.pruefpunkte || []).length > 0)) {
    content.push(subTitel('Öl-Auffangwanne'));
    pushModulPruefpunkte(content, oelMod, null, gedruckt);
  }

  // NSHV - nur, wenn sie geprueft wurde (Anna-Lena 21.09.2026). Keine NSHV oder
  // nichts beantwortet: Kapitel faellt ganz weg, auch nicht unter "Weitere Pruefpunkte".
  // Seit v106 (Migration 76) je Verteilung ein Kapitel (Untertitel = Bezeichnung
  // der Verteilung) - jede Verteilung wird fuer sich geprueft.
  const nshvMods = modByCode(module, '03-NSHV');
  const nshvGeprueft = (m) => {
    const punkte = m.pruefpunkte || [];
    const antwort = punkte.find(x => String(x.pos_nummer || '').trim() === '03')?.wert_auswahl;
    return antwort !== 'nicht vorhanden'
      && punkte.some(x => istBeantwortet(x) && String(x.pos_nummer || '').trim() !== '03');
  };
  const nshvDruck = nshvMods.filter(nshvGeprueft);
  if (nshvDruck.length > 0) {
    content.push(abschnitt(++nr, nshvDruck.length > 1 ? 'Niederspannungsverteilungen (NSHV)' : 'Niederspannungs-Hauptverteilung (NSHV)'));
    pushModulPruefpunkte(content, nshvDruck, 'Keine Prüfpunkte erfasst.', gedruckt);
  }
  nshvMods.forEach(m => gedruckt.add(m.id));

  // Stationszubehoer (Modul 05-SICHERHEITS-ZUBEHOER)
  content.push(abschnitt(++nr, 'Stationszubehör'));
  pushModulPruefpunkte(content, modByCode(module, '05-SICHERHEITS-ZUBEHOER'), 'Keine Prüfpunkte erfasst.', gedruckt);

  // Messung
  // Ohne Verrechnungsmessung reicht diese eine Zeile (Anna-Lena 21.09.2026) -
  // Wandler-Angaben gibt es dann nicht.
  const mitMessung = c.verrechnungsmessung === 'ms' || c.verrechnungsmessung === 'ns';
  content.push(zusammen(abschnitt(++nr, 'Messung'), kvTable(mitMessung ? [
    // Drei Werte (ms / ns / keine), kein Ja/Nein - wie in ausstattungTabelle().
    ['Verrechnungsmessung', VERRECHNUNG_TEXT[c.verrechnungsmessung] || c.verrechnungsmessung],
    ['Stromwandler', wandlerDa(c, wandler, 'strom') == null ? null : jaNein(wandlerDa(c, wandler, 'strom'))],
    ['Spannungswandler', wandlerDa(c, wandler, 'spannung') == null ? null : jaNein(wandlerDa(c, wandler, 'spannung'))],
    ['Wandler erfasst', Array.isArray(wandler) ? wandler.length : 0],
  ] : [
    ['Verrechnungsmessung', 'keine'],
  ]) || hinweisZeile('Keine Angaben.')));
  // Zaehler (Anna-Lena 08.10.2026, Migration 76): Bezeichnung, Zaehlernummer, Wandlerverhaeltnis.
  const zaehler = (Array.isArray(c.zaehler) ? c.zaehler : [])
    .filter(z => z && (z.bezeichnung || z.zaehlernummer || z.wandlerverhaeltnis));
  if (zaehler.length > 0) {
    content.push(zusammen(subTitel('Zähler'), datenTabelle(['*', '*', '*'],
      [kopfZelle('Bezeichnung'), kopfZelle('Zählernummer'), kopfZelle('Wandlerverhältnis')],
      zaehler.map(z => [
        { text: z.bezeichnung || '–', style: 'tdZelle' },
        { text: z.zaehlernummer || '–', style: 'tdZelle' },
        { text: z.wandlerverhaeltnis || '–', style: 'tdZelle' },
      ]))));
  }

  // Erdung
  content.push(abschnitt(++nr, 'Erdung / Erderanlage'));
  pushModulPruefpunkte(content, modByCode(module, '06-ERDERANLAGE'),
    c.erderanlage_vorhanden ? 'Keine Prüfpunkte erfasst.' : 'Nicht vorhanden.', gedruckt);
  // Schienen mit Abgaengen und Messwerten als Bild (wie im Kurzbericht).
  (erder || []).filter(sch => (sch.anschluesse || []).length > 0).forEach(sch => {
    const reihen = erderGrafik(sch, erderBewertungMit(erderGrenzeVon(c)));
    content.push(zusammen(
      { text: sch.bezeichnung || 'Erdungsschiene', bold: true, fontSize: 9, color: NAVY, margin: [0, 6, 0, 2] },
      reihen[0],
    ));
    reihen.slice(1).forEach(r => content.push(r));
  });

  // Thermografie
  content.push(abschnitt(++nr, 'Elektrothermografie'));
  pushModulPruefpunkte(content, modByCode(module, '07-ELEKTROTHERMOGRAFIE'),
    c.elektrothermografie_durchgef ? 'Keine Prüfpunkte erfasst.' : 'Nicht durchgeführt.', gedruckt);

  // Alle uebrigen Module mit Pruefpunkten (z.B. neue Modul-Vorlagen).
  // Regel: JEDES Modul des Protokolls mit Pruefpunkten steht im Vollprotokoll.
  const uebrigeModule = (module || []).filter(m => !gedruckt.has(m.id) && (m.pruefpunkte || []).length > 0);
  if (uebrigeModule.length > 0) {
    content.push(abschnitt(++nr, 'Weitere Prüfpunkte'));
    uebrigeModule.forEach((m) => {
      content.push(subTitel(m.modul_vorlagen?.name || m.modul_vorlagen?.code || 'Modul'));
      pushModulPruefpunkte(content, [m], null, gedruckt);
    });
  }

  // Bewertung & Abschluss
  // Fotodokumentation vor der Bewertung - der Abschluss bleibt das Letzte.
  nr = fotosAnhaengen(content, daten.fotoBilder, module, nr);

  content.push(zusammen(
    abschnitt(++nr, 'Bewertung und Abschluss'),
    bewertungTabelle(protokoll),
    empfehlungsBox(protokoll, maengel),
    protokoll?.abschluss_bemerkung ? [subTitel('Bemerkung'), { text: protokoll.abschluss_bemerkung, style: 'kWert' }] : null,
  ));
  content.push(unterschriftBlock(protokoll));

  return basisDocDef(content, artProtokoll(protokoll), protokoll, station);
}

// Wandler vorhanden? Seit 18.09.2026 gibt es im Stammdaten-Formular keine
// eigenen Haekchen mehr - die Wandler-Liste zaehlt, das alte Haekchen gilt weiter.
function wandlerDa(c, wandler, typ) {
  // Liegt die Liste vor, zaehlt NUR sie - das alte Haekchen blieb sonst ewig
  // auf "ja", auch wenn der Wandler entfernt wurde.
  if (Array.isArray(wandler)) return wandler.some(w => (w.wandler_typ || 'strom') === typ);
  const merker = typ === 'strom' ? c?.stromwandler_vorhanden : c?.spannungswandler_vorhanden;
  return merker == null ? null : !!merker;
}

// nurVorhandenes (Anna-Lena 19.09.2026): Zeilen mit "nein" verwirren den Kunden -
// im Protokoll steht nur noch, was es in der Station tatsaechlich gibt.
function ausstattungTabelle(config, wandler) {
  if (!config) return null;
  // Die Verrechnungsmessung ist KEINE Ja/Nein-Angabe, sondern hat drei Werte.
  const MESSUNG_TEXT = { ms: 'MS-Messung', ns: 'NS-Messung', keine: 'keine' };
  const messungVorhanden = config.verrechnungsmessung === 'ms' || config.verrechnungsmessung === 'ns';
  const items = [
    { label: 'MS-Anlage',           an: config.ms_anlage_vorhanden },
    { label: 'NSHV',                an: config.nshv_vorhanden },
    { label: 'Ölauffangwanne',      an: config.oelauffangwanne_vorhanden },
    { label: 'Erderanlage',         an: config.erderanlage_vorhanden },
    { label: 'Elektrothermografie', an: config.elektrothermografie_durchgef },
    { label: 'Stromwandler',        an: wandlerDa(config, wandler, 'strom') },
    { label: 'Spannungswandler',    an: wandlerDa(config, wandler, 'spannung') },
    { label: 'Verrechnungsmessung', an: messungVorhanden, text: MESSUNG_TEXT[config.verrechnungsmessung] || '–' },
  ].filter(it => it.an === true);
  if (items.length === 0) return null;
  const body = [];
  for (let i = 0; i < items.length; i += 2) {
    const zelle = (it) => (it ? [
      { text: it.label, style: 'kLabel' },
      { text: it.text || jaNein(it.an), style: 'kWert', bold: !!it.an, color: it.an ? GREEN : GRAY },
    ] : [{ text: '' }, { text: '' }]);
    body.push([...zelle(items[i]), ...zelle(items[i + 1])]);
  }
  return { table: { widths: [92, '*', 92, '*'], body }, layout: LAYOUT_RUHIG, margin: [0, 0, 0, 8] };
}

function trafoPaare(t, voll) {
  const isOel = t.trafoart && t.trafoart !== 'giessharz';
  const og = t.gesamt_oel_gewicht_kg ?? t.oel_gewicht_kg;
  return [
    ['Hersteller', t.hersteller], ['Herstellungsjahr', t.herstellungsjahr],
    ['Typ', t.typ], ['Leistung', zahlDe(t.leistung_kva, 'kVA')],
    ['Seriennummer', t.seriennummer], ['Trafo-Art', TRAFOART_LABEL[t.trafoart] || t.trafoart],
    // Die App schreibt os_spannung_v; betriebsspannung_kv stammt nur aus dem Import.
    voll ? ['OS-Spannung', fmtSpannungKv(t.os_spannung_v ?? t.betriebsspannung_kv)]
      : ['Betriebsspannung', fmtSpannungKv(t.os_spannung_v ?? t.betriebsspannung_kv)],
    voll ? ['US-Spannung', fmtSpannungKv(t.us_spannung_v)] : null,
    ['Schaltgruppe', t.schaltgruppe], ['uk', zahlDe(t.uk_prozent, '%')],
    ['Gesamtgewicht', zahlDe(t.gesamt_gewicht_kg, 'kg')],
    isOel ? ['Ölgewicht', zahlDe(og, 'kg')] : null,
    voll ? ['Pos. Schaltstufe', t.position_schaltstufe] : null,
  ];
}

// Schutzfunktionen des Trafos als eigene Tabelle, aufgebaut wie im
// Papierprotokoll: links die Schutzeinrichtungen, rechts Anschluss und
// Funktion. Vorher stand alles als Komma-Aufzaehlung in EINER Zelle
// (Anna-Lena 15.09.2026: "bitte anders aufbauen").
const MITNAHME_TEXT = { ok: 'in Ordnung', defekt: 'funktioniert nicht', nicht_benoetigt: 'nicht benötigt (Stand-alone)' };
function schutzTabelle(t) {
  const sf = t && t.schutzfunktionen && typeof t.schutzfunktionen === 'object' ? t.schutzfunktionen : null;
  const hatEtwas = sf && (Object.keys(SCHUTZ_LABEL).some(k => schutzVorhanden(sf[k]))
    || SCHUTZ_DREIWERTIG.some(([k]) => schutzJaNein(sf[k]) !== null) || hatWert(sf.mitnahmeschaltung));
  if (!hatEtwas && !hatWert(t?.sonstiges)) return null;

  const gut = (text) => ({ text, style: 'kWert', bold: true, color: GREEN });
  const schlecht = (text) => ({ text, style: 'kWert', bold: true, color: RED });
  const offen = () => ({ text: '–', style: 'kWert', color: GRAY });

  // Links: Schutzeinrichtungen
  const einrichtung = (key, label) => {
    const v = sf ? sf[key] : null;
    const typ = v && typeof v === 'object' && hatWert(v.typ) ? v.typ : null;
    return [{ text: label, style: 'kLabel' },
      schutzVorhanden(v) ? { text: [{ text: 'vorhanden', bold: true, color: GREEN }, typ ? { text: '  Typ ' + typ, color: GRAYDARK } : ''], style: 'kWert' }
        : { text: 'nicht vorhanden', style: 'kWert', color: GRAY }];
  };
  // Nur was bei dieser Trafo-Art ueberhaupt sein kann (Anna-Lena 19.09.2026):
  // Giessharz hat nur den Kaltleiter, Oel hat keinen. "nicht vorhanden" bei einer
  // unmoeglichen Einrichtung hat den Kunden irritiert - die Zeile faellt jetzt weg.
  const art = t?.trafoart || null;
  // Oel = jede Art ausser Giessharz (oel_ausdehner, oel_hermetik ...).
  const moeglich = (k) => (art === 'giessharz' ? k === 'kaltleiter'
    : art ? k !== 'kaltleiter' : true);
  const links = sf && sf.kein_schutz === true
    ? [[{ text: 'Kein Schutz vorhanden', colSpan: 2, style: 'kWert', bold: true, color: RED }, {}]]
    : [['vollschutz', 'Vollschutz'], ['buchholz', 'Buchholz'], ['thermometer', 'Thermometer'],
       ['kaltleiter', 'Kaltleiter']]
      // Unmoegliche Einrichtungen weglassen - aber nur, wenn dort auch nichts
      // erfasst ist. Sonst verschwiege das PDF einen eingetragenen Wert.
      .filter(([k]) => moeglich(k) || schutzVorhanden(sf ? sf[k] : null))
      .map(([k, label]) => einrichtung(k, label));

  // Rechts: Anschluss und Funktion
  const status = (key, label, ja, nein) => {
    const w = sf ? schutzJaNein(sf[key]) : null;
    return [{ text: label, style: 'kLabel' }, w === true ? gut(ja) : w === false ? schlecht(nein) : offen()];
  };
  const mit = sf ? sf.mitnahmeschaltung : null;
  const rechts = [
    status('schutz_am_trafo', 'Schutz am Trafo', 'angeschlossen', 'nicht angeschlossen'),
    status('schutz_an_ms_anlage', 'Schutz an MS-Anlage', 'angeschlossen', 'nicht angeschlossen'),
    status('ausloesespule_ms_anlage', 'Auslösespule MS-Anlage', 'vorhanden', 'nicht vorhanden'),
    [{ text: 'Mitnahmeschaltung', style: 'kLabel' },
     mit === 'ok' ? gut(MITNAHME_TEXT.ok) : mit === 'defekt' ? schlecht(MITNAHME_TEXT.defekt)
       : hatWert(mit) ? { text: MITNAHME_TEXT[mit] || String(mit), style: 'kWert' } : offen()],
  ];

  const zeilen = Math.max(links.length, rechts.length);
  const body = [[
    { text: 'Schutzeinrichtung', colSpan: 2, bold: true, fontSize: 8.5, color: GREEN, fillColor: GRAYBG }, {},
    { text: 'Anschluss und Funktion', colSpan: 2, bold: true, fontSize: 8.5, color: GREEN, fillColor: GRAYBG }, {},
  ]];
  for (let i = 0; i < zeilen; i++) {
    body.push([...(links[i] || [{ text: '' }, { text: '' }]), ...(rechts[i] || [{ text: '' }, { text: '' }])]);
  }
  if (hatWert(t?.sonstiges)) {
    body.push([{ text: 'Sonstiges', style: 'kLabel' }, { text: t.sonstiges, colSpan: 3, style: 'kWert' }, {}, {}]);
  }
  return { table: { widths: [92, '*', 92, '*'], body, dontBreakRows: true }, layout: LAYOUT_RUHIG, margin: [0, 0, 0, 8] };
}

// Fotos ans Dokument haengen - je zwei nebeneinander. Fotos, die an einem
// n.i.O.-Pruefpunkt haengen, dokumentieren einen Mangel und stehen deshalb in
// einem eigenen Abschnitt mit Pos.-Nummer (Anna-Lena 19.09.2026). Gibt die neue
// Abschnittsnummer zurueck.
function fotosAnhaengen(content, fotoBilder, module, nr) {
  // Nur echte Bild-Daten (siehe ladeFotoBilder) - ein kaputter Eintrag wuerde
  // sonst das ganze PDF verschlucken.
  const bilder = (fotoBilder || []).filter(f => /^data:image\//.test(String(f?.datenUrl || '')));
  if (bilder.length === 0) return nr;
  const ppNach = new Map();
  (module || []).forEach(m => (m.pruefpunkte || []).forEach(pp => ppNach.set(pp.id, pp)));
  const mangelhaft = (f) => ppNach.get(f.pruefpunkt_id)?.in_ordnung === false;
  const unterschrift = (f) => {
    const pp = ppNach.get(f.pruefpunkt_id);
    if (!pp) return f.titel || '';
    const wo = ohneDoppelpunkt(pp.bezeichnung);
    return [wo, f.titel && f.titel !== pp.bezeichnung ? f.titel : ''].filter(Boolean).join(': ');
  };
  const galerie = (liste, rot) => {
    const teile = [];
    for (let i = 0; i < liste.length; i += 2) {
      const paar = liste.slice(i, i + 2);
      teile.push(zusammen({
        columns: paar.map(f => ({
          width: '*',
          stack: [
            { image: f.datenUrl, fit: [245, 185] },
            { text: unterschrift(f), style: 'tdZelleKlein', color: rot ? RED : GRAYDARK, margin: [0, 3, 0, 0] },
          ],
        })).concat(paar.length === 1 ? [{ width: '*', text: '' }] : []),
        columnGap: 14,
        margin: [0, 0, 0, 10],
      }));
    }
    return teile;
  };
  // Die Ueberschrift gehoert IN den ersten Block (sonst steht sie allein am
  // Seitenende, siehe CLAUDE.md "PDF-Gestaltung").
  const mitKopfzeile = (teile, kopf) => {
    if (teile.length === 0) return [];
    return [zusammen(kopf, teile[0]), ...teile.slice(1)];
  };
  const mangelBilder = bilder.filter(mangelhaft);
  const weitere = bilder.filter(f => !mangelhaft(f));
  let n = nr;
  if (mangelBilder.length > 0) {
    content.push(...mitKopfzeile(galerie(mangelBilder, true), abschnitt(++n, 'Fotos zu den festgestellten Mängeln')));
  }
  if (weitere.length > 0) {
    content.push(...mitKopfzeile(galerie(weitere, false),
      abschnitt(++n, mangelBilder.length > 0 ? 'Weitere Fotos' : 'Fotodokumentation')));
  }
  return n;
}

// Die Kopfzeilen des Papierprotokolls (Oelprobe, Schutzpruefung, Aggregat,
// Pruefplakette). Stehen im Kundenbericht UND - seit 20.09.2026, weil das
// Vollprotokoll das Archivdokument ist - auch dort.
function angabenZurWartung(protokoll, config, msFelder, trafos) {
  const pk = protokoll || {};
  const lage = kopfFragenLage(config, msFelder, trafos);
  const angaben = [
    (!lage.nurGh || pk.ist_oelprobe_entnommen === 'ja')
      && ['Ölprobe entnommen', pk.ist_oelprobe_entnommen, pk.ist_oelprobe_entnommen === 'nicht_moeglich' ? pk.oelprobe_grund : null],
    (lage.hatLs || pk.ist_schutzpruefung === 'ja') && ['Schutzprüfung durchgeführt', pk.ist_schutzpruefung],
    ['Aggregat für Wartungsmaschinen', pk.ist_aggregat_erforderlich],
    pk.ist_aggregat_notstrom != null && ['Aggregat als Notstrom (Kundennetz)', pk.ist_aggregat_notstrom,
      pk.ist_aggregat_notstrom === 'ja' && pk.aggregat_notstrom_kva != null
        ? 'Leistung ' + String(pk.aggregat_notstrom_kva).replace('.', ',') + ' kVA' : null],
    ['Prüfplakette angebracht', pk.ist_pruefplakette],
  ].filter(Boolean);
  return {
    table: { widths: angaben.map(() => '*'), body: [angaben.map(([label, w, zusatz]) => ({ stack: [
      { text: label, style: 'kLabel' },
      { text: JA_NEIN_TEXT[w] || '–', bold: true, fontSize: 10, color: w === 'ja' ? NAVY : GRAYDARK, margin: [0, 2, 0, 0] },
      ...(zusatz ? [{ text: zusatz, fontSize: 8, color: GRAYDARK, margin: [0, 1, 0, 0] }] : []),
    ] }))] },
    layout: { hLineWidth: () => 0, vLineWidth: (i) => (i === 0 ? 0 : 0.5), vLineColor: () => GRAYLINE,
      paddingTop: () => 2, paddingBottom: () => 2, paddingLeft: (i) => (i === 0 ? 0 : 9), paddingRight: () => 6 },
    margin: [0, 0, 0, 6],
  };
}

// Gesamtzustand als Skala ++ + 0 - --, der gewaehlte Wert farbig.
function zustandSkala(wert, breite = 22) {
  const stufen = ['++', '+', '0', '-', '--'];
  return {
    table: { widths: stufen.map(() => breite), body: [stufen.map(st => (st === wert
      ? { text: st, bold: true, fontSize: 9, alignment: 'center', color: '#FFFFFF', fillColor: ZUSTAND_FARBE[st] }
      : { text: st, fontSize: 9, alignment: 'center', color: '#A9B3C4', fillColor: GRAYBG }))] },
    layout: { hLineWidth: () => 0, vLineWidth: (i) => (i === 0 || i === stufen.length ? 0 : 2),
      vLineColor: () => '#FFFFFF', paddingTop: () => 3, paddingBottom: () => 3, paddingLeft: () => 0, paddingRight: () => 0 },
  };
}

// Der gruene bzw. rote Kasten "Unsere Empfehlung" - im Kundenbericht UND seit
// 19.09.2026 auch im Vollprotokoll (Anna-Lena: "das ist richtig gut").
function empfehlungsBox(protokoll, maengel) {
  const p = protokoll || {};
  const e = handlungsempfehlung(p, maengel);
  // Drei Stufen statt zwei (Anna-Lena 19.09.2026): Gruen hiess bisher auch dann
  // "alles gut", wenn im Text stand, dass die Sicherheit von Personen nicht
  // gewaehrleistet ist. Ist etwas nicht gewaehrleistet oder gibt es offene
  // Maengel, wird der Kasten orange - rot bleibt dem dringenden Fall vorbehalten.
  const nichtGewaehrleistet = !!(p.sicherheit_personen_n_g || p.sicherheit_anlagen_n_g
    || p.sicherheit_betrieb_n_g || p.sicherheit_netz_n_g);
  const offeneMaengel = (maengel || []).filter(m => !m.erledigt).length > 0 || !!p.handlungsbedarf;
  const farbe = e.dringend ? RED : (nichtGewaehrleistet || offeneMaengel) ? ORANGE : GREEN;
  const hintergrund = e.dringend ? REDBG : (nichtGewaehrleistet || offeneMaengel) ? ORANGEBG : GREENBG;
  return {
    table: { widths: ['*'], body: [[{
      stack: [
        { text: 'Unsere Empfehlung', bold: true, fontSize: 9.5, color: farbe, margin: [0, 0, 0, 4] },
        ...e.absaetze.map(txt => ({ text: txt, style: 'kWert', margin: [0, 0, 0, 3] })),
      ],
    }]] },
    layout: {
      fillColor: () => hintergrund,
      hLineWidth: () => 0, vLineWidth: (i) => (i === 0 ? 3 : 0),
      vLineColor: () => farbe,
      paddingTop: () => 8, paddingBottom: () => 6, paddingLeft: () => 11, paddingRight: () => 10,
    },
    margin: [0, 4, 0, 8],
  };
}

function bewertungTabelle(protokoll) {
  const p = protokoll || {};
  // Nur Zeilen, die NICHT gewaehrleistet sind (Anna-Lena 21.09.2026) - ein leeres
  // Haekchen ist keine Bestaetigung.
  const sicherheit = (label, schlecht) => (schlecht ? [
    { text: label, style: 'kLabel' },
    { text: 'nicht gewährleistet', style: 'kWert', bold: true, color: RED },
  ] : null);
  return {
    table: { widths: [150, '*'], dontBreakRows: true, body: [
      [{ text: 'Gesamtzustand der Anlage', style: 'kLabel', margin: [0, 3, 0, 0] },
       { columns: [
         { width: 'auto', stack: [zustandSkala(p.bewertung_gesamtzustand)] },
         { width: '*', text: GESAMTZUSTAND_TEXT[p.bewertung_gesamtzustand] || '–', bold: true, margin: [8, 3, 0, 0],
           color: ZUSTAND_FARBE[p.bewertung_gesamtzustand] || NAVY },
       ] }],
      [{ text: 'Empfohlener Wartungszyklus', style: 'kLabel' },
       { text: !p.bewertung_wartungszyklus_jahre ? '–' : String(p.bewertung_wartungszyklus_jahre).replace(/\D/g, '') === '1' ? 'jährlich' : 'alle ' + p.bewertung_wartungszyklus_jahre + ' Jahre', style: 'kWert' }],
      // Sichtpruefungen bis zur naechsten Wartung - nur wenn empfohlen (Migration 59).
      p.empfohlene_sichtpruefungen ? [{ text: 'Empfohlene Sichtprüfungen', style: 'kLabel' },
       { text: sichtpruefungText(p.empfohlene_sichtpruefungen), style: 'kWert' }] : null,
      sicherheit('Personensicherheit', p.sicherheit_personen_n_g),
      sicherheit('Anlagensicherheit', p.sicherheit_anlagen_n_g),
      sicherheit('Betriebssicherheit', p.sicherheit_betrieb_n_g),
      sicherheit('Netzsicherheit', p.sicherheit_netz_n_g),
      // Eine Zeile, zwei Stufen (Anna-Lena 21.09.2026, Migration 65).
      [{ text: 'Handlungsbedarf', style: 'kLabel' },
       p.dringender_handlungsbedarf
         ? { text: 'dringend – Mängel umgehend beseitigen', style: 'kWert', bold: true, color: RED }
         : p.handlungsbedarf
           ? { text: 'ja – Mängel zeitnah beseitigen', style: 'kWert', bold: true, color: ORANGE }
           : { text: 'nein', style: 'kWert', color: NAVY }],
    ].filter(Boolean) },
    layout: LAYOUT_RUHIG,
    margin: [0, 0, 0, 8],
  };
}

// ---------------------------------------------------------------------------
// Maengelliste
// ---------------------------------------------------------------------------
// Maengel werden bewusst NICHT eingestuft (Anna-Lena, 17.09.2026). Die Spalte
// schweregrad in der Datenbank bleibt, wird aber nirgends mehr gezeigt.

// Wo steht der Mangel? "Stationszubehör · Pos. 05.2" - aus dem zugehoerigen Pruefpunkt.
function mangelOrt(m, module, mitPos) {
  if (!m.pruefpunkt_id) return '';
  for (const mod of module || []) {
    const pp = (mod.pruefpunkte || []).find(x => x.id === m.pruefpunkt_id);
    if (pp) {
      return [mod.bezugs_label || mod.modul_vorlagen?.name,
        mitPos && pp.pos_nummer ? 'Pos. ' + (pp.anzeige_nummer || pp.pos_nummer) : null].filter(Boolean).join(' · ');
    }
  }
  return '';
}

// mitPos: nur in der Maengelliste (fuer das Angebot im Buero). Im Kundenbericht
// steht nur, WO etwas ist (Kapitel/Trafo), nicht die Positionsnummer.
function maengelTabelle(maengel, module, { mitPos = false, nichtGedruckt = null } = {}) {
  const liste = (maengel || []).filter(m => !m.erledigt);
  if (liste.length === 0) return null;
  const zeilen = liste.map((m, i) => {
    const ort = mangelOrt(m, module, mitPos);
    // Nur in der internen Maengelliste: Hinweis, dass dieser Punkt im
    // Kundendokument nicht erscheint (Anna-Lena 14.10.2026).
    const ausgenommen = !!(nichtGedruckt && m.pruefpunkt_id && nichtGedruckt.has(m.pruefpunkt_id));
    return [
      { text: String(i + 1), style: 'tdZelle', alignment: 'center', bold: true },
      { stack: [
        { text: ohneDoppelpunkt(m.titel), style: 'tdZelle', bold: true },
        ort ? { text: ort, fontSize: 7.5, color: GRAY, margin: [0, 1, 0, 0] } : null,
        ausgenommen ? { text: 'nicht im Kundenprotokoll', fontSize: 7.5, color: ORANGE, bold: true, margin: [0, 1, 0, 0] } : null,
      ].filter(Boolean) },
      { text: kundenText(m.beschreibung), style: 'tdZelleKlein' },
    ];
  });
  return datenTabelle([20, '*', 220],
    [kopfZelle('Nr.', { alignment: 'center' }), kopfZelle('Mangel'), kopfZelle('Hinweis')],
    zeilen);
}

export function buildMaengelDocDef(daten) {
  const { protokoll, kunde, station, maengel, module } = daten;
  const content = [];
  const stName = station?.bezeichnung || protokoll?.stationsbezeich || '';
  const k = kunde || {};
  content.push(...titelKopf('Mängelliste', stName, [k.name, artWort(protokoll) + ' am ' + fmtDatum(protokoll?.wartungs_datum)]));
  const kopf = kvTable([
    ['Kunde', k.name], ['Kundennr.', k.kunden_nr],
    ['Station', station?.bezeichnung || protokoll?.stationsbezeich],
    ['Standort', anschrift(station?.standort_strasse, station?.standort_plz, station?.standort_ort)],
  ]);
  if (kopf) content.push(kopf);

  const tabelle = maengelTabelle(maengel, module, { mitPos: true, nichtGedruckt: daten.nichtGedruckt });
  if (!tabelle) {
    content.push({ text: 'Keine offenen Mängel festgestellt.', style: 'leer', margin: [0, 12, 0, 0] });
    return basisDocDef(content, 'Mängelliste', protokoll, station);
  }
  const anzahl = (maengel || []).filter(m => !m.erledigt).length;
  content.push({ text: 'Bei der Wartung wurden ' + anzahl + (anzahl === 1 ? ' Mangel' : ' Mängel')
    + ' festgestellt. Diese Liste dient als Grundlage für ein Instandsetzungs-Angebot.', style: 'hinweis', margin: [0, 4, 0, 10] });
  content.push(tabelle);
  return basisDocDef(content, 'Mängelliste', protokoll, station);
}

// ---------------------------------------------------------------------------
// Wartungsbericht fuer den Kunden
// ---------------------------------------------------------------------------
// Das Vollprotokoll spiegelt die Abfrage 1:1 und wird schnell 15 Seiten lang.
// Die meisten Kunden interessiert aber nur: Was ist meine Anlage, was ist nicht
// in Ordnung, was soll ich tun. Genau das steht hier.
const ISOLIERART_TEXT = { luft: 'Luft (gekapselt)', luft_offen: 'Luft, offene Bauweise ohne Berührungsschutz', sf6: 'SF6-Gas', clean_air: 'Clean Air' };
const VERRECHNUNG_TEXT = { ms: 'MS-Messung', ns: 'NS-Messung', keine: 'keine vorhanden' };
const JA_NEIN_TEXT = { ja: 'ja', nein: 'nein', n_benoetigt: 'nicht benötigt', nicht_moeglich: 'nicht möglich' };
// Empfehlung, wie oft bis zur naechsten Wartung sichtgeprueft werden soll.
const sichtpruefungText = (n) => ({ 2: 'zweimal', 3: 'dreimal' }[Number(n)] || 'einmal') + ' bis zur nächsten Wartung';

// Welche Schutzfunktionen sind am Trafo angekreuzt?
const SCHUTZ_LABEL = {
  vollschutz: 'Vollschutz', buchholz: 'Buchholz', thermometer: 'Thermometer',
  kaltleiter: 'Kaltleiter', kein_schutz: 'kein Schutz vorhanden',
};
// Die Schutzfunktionen stehen in ZWEI Formen in der Datenbank: der Import
// speichert flache Wahrheitswerte ({ vollschutz: true }), der Trafo-Editor
// Objekte ({ vollschutz: { vorhanden: true, typ: 'XYZ' } }). Frueher zaehlte
// nur "=== true" - die Angaben aus dem Editor fehlten dadurch im PDF.
function schutzVorhanden(v) { return v === true || v?.vorhanden === true; }
// Dreiwertige Felder: true bzw. 'a' = ja, false bzw. 'b' = nein, sonst unbekannt.
function schutzJaNein(v) {
  if (v === true || v === 'a') return true;
  if (v === false || v === 'b') return false;
  return null;
}
const SCHUTZ_DREIWERTIG = [
  ['schutz_am_trafo', 'Schutz am Trafo angeschlossen', 'Schutz am Trafo nicht angeschlossen'],
  ['schutz_an_ms_anlage', 'Schutz an MS-Anlage angeschlossen', 'Schutz an MS-Anlage nicht angeschlossen'],
  ['ausloesespule_ms_anlage', 'Auslösespule an MS-Anlage vorhanden', 'keine Auslösespule an MS-Anlage'],
];
// Textbausteine aus dem Papierprotokoll, wortgleich uebernommen. Sie stehen
// dort unter der Erderanlage und sind rechtlich abgestimmt - deshalb hier
// unveraendert und nicht neu formuliert.
const ERDER_BEMERKUNG_MANGEL =
  'Die Erdungsanlage entspricht nicht den Anforderungen der TAB des zuständigen '
  + 'Netzbetreibers sowie den Normen der DIN VDE (z. B. DIN VDE 0100-540, '
  + 'DIN VDE 0101) und den Prüfpflichten nach DIN VDE 0105-100 sowie § 5 Abs. 1 '
  + 'Nr. 1 der DGUV Vorschrift 3. Dadurch ist die Wirksamkeit des Schutzes vor '
  + 'gefährlichen Berührungsspannungen bei Erdfehlern und anderen elektrischen '
  + 'Fehler- oder Störfällen nicht sichergestellt. Dies gefährdet sowohl die '
  + 'elektrische Sicherheit als auch die Personen- und Anlagensicherheit und kann '
  + 'im Fehlerfall zu Personen-, Sachschäden und Betriebsstörungen führen.';
const ERDER_MASSNAHME_MANGEL =
  'Unverzügliche Instandsetzung bzw. Anpassung der Erdungsanlage an die geltenden '
  + 'TAB und DIN-VDE-Bestimmungen. Nach Abschluss ist eine Wiederholungsprüfung '
  + 'zur Bestätigung der normgerechten Ausführung erforderlich.';
// Standard 2 Ohm, je Station aenderbar (station_konfiguration.erder_grenzwert_ohm, 18.09.2026).
const ERDER_GRENZE_STANDARD = 2.0;
function erderGrenzeVon(c) {
  // Komma-Eingaben ("2,5") kamen sonst als NaN an und fielen still auf 2 Ω zurueck.
  const n = Number(String(c?.erder_grenzwert_ohm ?? '').replace(',', '.'));
  return c?.erder_grenzwert_ohm != null && isFinite(n) && n > 0 ? n : ERDER_GRENZE_STANDARD;
}

// Messbefund wie in der App (ErderanlageBlock): gemessen, ueber der Grenze, schlechtester.
function erderBefund(erder, grenze) {
  let anzahlGemessen = 0; let anzahlUeberGrenze = 0; let maxOhm = null; let maxOl = false; let schlechtester = null;
  for (const s of erder || []) {
    for (const a of s.anschluesse || []) {
      const m = a.messung;
      if (!m) continue;
      if (m.ueber_messbereich === true) {
        anzahlGemessen++; anzahlUeberGrenze++;
        if (!maxOl) { maxOl = true; schlechtester = a.bezeichnung || 'Anschluss'; }
        continue;
      }
      if (m.ohm_wert == null || m.ohm_wert === '') continue;
      const wert = Number(String(m.ohm_wert).replace(',', '.'));
      if (!isFinite(wert)) continue;
      anzahlGemessen++;
      if (!maxOl && (maxOhm === null || wert > maxOhm)) { maxOhm = wert; schlechtester = a.bezeichnung || 'Anschluss'; }
      if (wert > grenze) anzahlUeberGrenze++;
    }
  }
  return anzahlGemessen ? { anzahlGemessen, anzahlUeberGrenze, maxOhm, maxOl, schlechtester, grenze } : null;
}
// Derselbe Satz wie in ErderMangelSync (EditableStammdaten), ohne "Auto-Bewertung:".
function erderAutoText(b) {
  const ohm = (n) => String(n).replace('.', ',');
  return (b.maxOl ? 'OL (über Messbereich)' : ohm(b.maxOhm) + ' Ω') + ' an "' + b.schlechtester
    + '" über Grenzwert ' + ohm(b.grenze) + ' Ω (' + b.anzahlUeberGrenze + ' von ' + b.anzahlGemessen + ' Anschlüssen).';
}

// Erderanlage als Bild statt Tabelle (Anna-Lena, 17.09.2026): je Schiene EIN
// Balken mit ALLEN Abgaengen in einer Reihe. Beschriftung und Messwert stehen
// senkrecht, damit auch viele Abgaenge nebeneinander passen.
const ERDER_BREITE = 511;   // Satzspiegel A4 mit 42 pt Rand links und rechts
function xmlText(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
function kuerzen(text, max) {
  const t = String(text || '').replace(/\s+/g, ' ').trim();
  return t.length > max ? t.slice(0, max - 1) + '…' : t;
}
// OL = Ueberlauf am Messgeraet: gemessen UND ueber der Grenze.
const erderBewertungMit = (grenze) => (a) => {
  const ol = a.messung?.ueber_messbereich === true;
  const gemessen = ol || a.messung?.ohm_wert != null;
  const ueber = ol || (a.messung?.ohm_wert != null && Number(a.messung.ohm_wert) > grenze);
  return { gemessen, ol, ueber };
};
const erderBewertung = erderBewertungMit(ERDER_GRENZE_STANDARD);
export function erderGrafik(schiene, bewertung = erderBewertung) {
  const liste = schiene.anschluesse || [];
  if (liste.length === 0) return [];
  // Aufbau je Abgang (Anna-Lena 18.09.2026): Schiene - Messwert waagerecht -
  // Kasten mit der Bezeichnung senkrecht - Ergebnis unten. Der Wert steht oben
  // und ist normal lesbar, die Bezeichnung braucht die Hoehe.
  const spalte = Math.min(56, (ERDER_BREITE - 20) / liste.length);
  const kastenBreite = Math.min(Math.max(spalte - 6, 10), 26);
  const wertY = 12, schieneY = 18, kastenOben = 40, kastenHoehe = 132;
  const hoehe = kastenOben + kastenHoehe + 18;
  const schrift = spalte >= 24 ? 8 : 7;
  const wertSchrift = spalte >= 30 ? 8 : spalte >= 22 ? 7 : 6;
  const zweiZeilen = kastenBreite >= 22;            // langer Name auf zwei Zeilen
  // Roboto braucht im Schnitt gut 0,55 x Schriftgroesse je Zeichen.
  const maxZeichen = Math.floor((kastenHoehe - 12) / (schrift * 0.58));
  let svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${ERDER_BREITE}" height="${hoehe}" viewBox="0 0 ${ERDER_BREITE} ${hoehe}">`;
  svg += `<rect x="10" y="${schieneY}" width="${liste.length * spalte}" height="11" rx="2" fill="${ANTHRAZIT}"/>`;
  liste.forEach((a, i) => {
    const { gemessen, ol, ueber } = bewertung(a);
    const farbe = !gemessen ? GRAY : ueber ? RED : GREEN;
    const grund = !gemessen ? GRAYBG : ueber ? REDBG : GREENBG;
    const cx = 10 + spalte * (i + 0.5);
    const mitte = kastenOben + kastenHoehe / 2;
    // Messwert UEBER der Schiene, Bezeichnung darunter (Anna-Lena 18.09.2026).
    // "nicht gemessen" passt oben nicht - dafuer steht unten das Ergebnis "-".
    const wert = ol ? 'OL' : gemessen ? String(a.messung.ohm_wert).replace('.', ',') + ' Ω' : '–';
    const vollName = String(a.bezeichnung || ('Anschluss ' + (i + 1))).replace(/\s+/g, ' ').trim();
    const name = kuerzen(vollName, maxZeichen);
    // Zu lang und genug Platz: an einem Leerzeichen moeglichst mittig teilen.
    let teil1 = null, teil2 = null;
    if (zweiZeilen && vollName.length > maxZeichen) {
      const mitteZ = vollName.length / 2;
      let pos = -1;
      for (let k = 0; k < vollName.length; k++) {
        if (vollName[k] === ' ' && (pos < 0 || Math.abs(k - mitteZ) < Math.abs(pos - mitteZ))) pos = k;
      }
      if (pos > 0) { teil1 = kuerzen(vollName.slice(0, pos), maxZeichen); teil2 = kuerzen(vollName.slice(pos + 1), maxZeichen); }
    }
    const ergebnis = !gemessen ? '–' : ueber ? 'n.i.O.' : 'i.O.';
    const senkrecht = (x, text, extra) =>
      `<text transform="translate(${x} ${mitte}) rotate(-90)" font-family="Roboto" font-size="${schrift}" text-anchor="middle" ${extra}>${xmlText(text)}</text>`;
    svg += `<text x="${cx}" y="${wertY}" font-family="Roboto" font-size="${wertSchrift}" font-weight="bold" fill="${farbe}" text-anchor="middle">${xmlText(wert)}</text>`
      + `<circle cx="${cx}" cy="${schieneY + 5.5}" r="2.4" fill="#FFFFFF"/>`
      + `<line x1="${cx}" y1="${schieneY + 11}" x2="${cx}" y2="${kastenOben}" stroke="${farbe}" stroke-width="1.5"/>`
      + `<rect x="${cx - kastenBreite / 2}" y="${kastenOben}" width="${kastenBreite}" height="${kastenHoehe}" rx="3" fill="${grund}" stroke="${farbe}" stroke-width="1.1"/>`
      + (teil1
        ? senkrecht(cx - schrift / 2 - 1, teil1, `fill="${NAVY}"`) + senkrecht(cx + schrift / 2 + 1, teil2, `fill="${NAVY}"`)
        : senkrecht(cx, name, `fill="${NAVY}"`))
      + `<text x="${cx}" y="${kastenOben + kastenHoehe + 12}" font-family="Roboto" font-size="${spalte >= 26 ? 7.5 : 6}" font-weight="bold" fill="${farbe}" text-anchor="middle">${ergebnis}</text>`;
  });
  svg += '</svg>';
  return [{ svg, width: ERDER_BREITE, margin: [0, 0, 0, 4] }];
}
// Jahreszahl je Betriebsmittel fuer den Satz im Bericht.
const LEBENSDAUER_TEXT = {
  trafo: LEBENSDAUER_JAHRE.trafo,
  ms_anlage: LEBENSDAUER_JAHRE.ms_anlage,
  nshv: LEBENSDAUER_JAHRE.nshv,
};
const STATIONSTYP_TEXT = {
  kompakt: 'Kompaktstation', begehbar: 'Begehbare Trafostation',
  im_gebaeude: 'Station im Gebäude', container: 'Containerstation',
  baustrom: 'Baustromstation', sonstige: 'Sonstige Bauform',
};

// Kachel fuer "Auf einen Blick".
function kachel(titel, inhalt, farbe) {
  return {
    stack: [
      { text: titel.toUpperCase(), fontSize: 7, bold: true, color: GRAY, characterSpacing: 0.6, margin: [0, 0, 0, 5] },
      ...inhalt,
    ],
    _farbe: farbe,
  };
}

function aufEinenBlick(protokoll, maengel) {
  const p = protokoll || {};
  const offen = (maengel || []).filter(m => !m.erledigt);
  const zustand = p.bewertung_gesamtzustand;
  const sicherheit = [
    ['Personen', p.sicherheit_personen_n_g], ['Anlage', p.sicherheit_anlagen_n_g],
    ['Betrieb', p.sicherheit_betrieb_n_g], ['Netz', p.sicherheit_netz_n_g],
  ];
  const sicherAlle = sicherheit.every(([, schlecht]) => !schlecht);
  const punkt = (farbe) => ({ canvas: [{ type: 'ellipse', x: 3.5, y: 5.5, r1: 3.5, r2: 3.5, color: farbe }], width: 12 });

  const zyklus = p.bewertung_wartungszyklus_jahre;
  let faellig = null;
  const m = String(p.wartungs_datum || '').match(/^(\d{4})-(\d{2})/);
  if (zyklus && m) faellig = m[2] + '/' + (Number(m[1]) + Number(zyklus));

  const kacheln = [
    kachel('Gesamtzustand', [
      zustandSkala(zustand, 19),
      { text: GESAMTZUSTAND_TEXT[zustand] || 'nicht bewertet', bold: true, fontSize: 11,
        color: ZUSTAND_FARBE[zustand] || GRAY, margin: [0, 5, 0, 0] },
    ]),
    kachel('Festgestellte Mängel', [
      { text: String(offen.length), fontSize: 22, bold: true, color: offen.length ? ORANGE : GREEN, lineHeight: 1 },
      { text: offen.length === 0 ? 'keine Mängel' : offen.length === 1 ? 'Mangel festgestellt' : 'Mängel festgestellt',
        fontSize: 8.5, color: GRAYDARK },
    ]),
    // Nur was NICHT gewaehrleistet ist (Anna-Lena 21.09.2026): Ohne Angabe stand hier
    // bisher "gewaehrleistet" - obwohl das niemand bestaetigt hatte.
    kachel('Sicherheit', sicherAlle
      ? [{ text: 'keine Einschränkung vermerkt', fontSize: 8.5, color: GRAY }]
      : sicherheit.filter(([, schlecht]) => schlecht).map(([label]) => ({
        columns: [punkt(RED), { text: label + ' – nicht gewährleistet', fontSize: 8, color: RED, bold: true }],
        margin: [0, 0, 0, 1],
      }))),
    kachel('Nächste Wartung', [
      { text: zyklus ? 'in ' + zyklus + (Number(zyklus) === 1 ? ' Jahr' : ' Jahren') : '–', fontSize: 13, bold: true, color: NAVY },
      faellig ? { text: 'fällig ca. ' + faellig, fontSize: 8.5, color: GRAYDARK, margin: [0, 2, 0, 0] } : null,
      p.empfohlene_sichtpruefungen ? { text: (Number(p.empfohlene_sichtpruefungen) || 1) + '× Sichtprüfung dazwischen',
        fontSize: 8.5, color: GRAYDARK, margin: [0, 2, 0, 0] } : null,
      p.dringender_handlungsbedarf ? { text: 'Mängel umgehend beseitigen', fontSize: 8.5, bold: true, color: RED, margin: [0, 4, 0, 0] }
        : p.handlungsbedarf ? { text: 'Mängel zeitnah beseitigen', fontSize: 8.5, bold: true, color: ORANGE, margin: [0, 4, 0, 0] } : null,
    ].filter(Boolean)),
  ];
  const randFarbe = (i) => {
    if (i === 0) return ZUSTAND_FARBE[zustand] || GRAYLINE;
    if (i === 1) return offen.length ? ORANGE : GREEN;
    if (i === 2) return sicherAlle ? GRAYLINE : RED;
    return p.dringender_handlungsbedarf ? RED : p.handlungsbedarf ? ORANGE : GRAYLINE;
  };
  return {
    table: { widths: ['*', '*', '*', '*'], body: [kacheln.map(({ _farbe, ...z }) => z)] },
    layout: {
      hLineWidth: (i) => (i === 0 ? 3 : 0.8),
      hLineColor: (i, node, spalte) => (i === 0 ? randFarbe(spalte) : GRAYLINE),
      vLineWidth: () => 0,
      paddingTop: () => 8, paddingBottom: () => 9, paddingLeft: () => 9, paddingRight: () => 9,
    },
    margin: [0, 0, 0, 4],
  };
}

export function buildKurzberichtDocDef(daten) {
  const { protokoll, kunde, vnb, station, config, trafos, msFelder, module, maengel, erder } = daten;
  const content = [];
  // Kopf-Angaben (Anna-Lena 18.09.2026): Schutzpruefung nur mit Leistungsschalter,
  // Oelprobe nicht bei reinen GH-Stationen (ausser es steht "ja" drin), Notstrom nur
  // wenn beantwortet (aeltere Protokolle kennen die Frage nicht).
  const k = kunde || {};
  const s = station || {};
  const c = config || {};
  const p = protokoll || {};
  const stName = s.bezeichnung || p.stationsbezeich || '';
  let nr = 0;

  content.push(...titelKopf(artProtokoll(p) + ' Trafostation', stName, [
    k.name,
    artWort(p) + ' am ' + fmtDatum(p.wartungs_datum) + (hatWert(p.monteure || p.monteur_unterschrift_name)
      ? '  ·  durchgeführt von ' + (p.monteure || p.monteur_unterschrift_name) : ''),
  ]));

  // Auf einen Blick
  content.push(zusammen(
    { text: 'Auf einen Blick', bold: true, fontSize: 12, color: NAVY, margin: [0, 0, 0, 6] },
    aufEinenBlick(p, maengel),
  ));

  // 1 · Auftrag
  const adressBlock = (titel, zeilen) => ({
    stack: [
      { text: titel, style: 'kLabel', margin: [0, 0, 0, 3] },
      ...zeilen.filter(hatWert).map((z, i) => ({ text: z, fontSize: 9, bold: i === 0, color: NAVY })),
    ],
  });
  const objektName = s.objekt_abweichend ? (s.objekt_name || stName) : (k.name || stName);
  const objektAnschrift = s.objekt_abweichend
    ? anschrift(s.objekt_strasse, s.objekt_plz, s.objekt_ort)
    : anschrift(s.standort_strasse, s.standort_plz, s.standort_ort);
  content.push(zusammen(
    abschnitt(++nr, 'Auftrag und Objekt'),
    {
      columns: [
        adressBlock('Auftraggeber', [k.name, k.strasse, [k.plz, k.ort].filter(hatWert).join(' '),
          k.ansprechpartner, k.telefon, k.email]),
        adressBlock('Auftragnehmer', ['EnergieTechnik Becker GmbH', 'Eugen-Langen-Straße 4', 'D-76227 Karlsruhe',
          'Telefon 0721 / 968 68 58-0', 'info@etb-karlsruhe.de']),
      ],
      columnGap: 20,
      margin: [0, 0, 0, 10],
    },
    kvTable([
      ['Objekt', objektName], ['Kundennummer', k.kunden_nr],
      ['Anschrift', objektAnschrift], ['Wartungsdatum', fmtDatum(p.wartungs_datum)],
      ['Station', stName], ['Wartungszyklus bisher', wartungszyklusText(s.wartungs_zyklus)],
      // Das Stationsformular schreibt den VNB-Namen in stations_nr, aeltere Importe in vnb_stationsbezeichnung.
      ['Bezeichnung beim VNB', s.vnb_stationsbezeichnung || s.stations_nr],
      ['Standort der Station', s.standort_beschreibung],
      ['Ansprechpartner vor Ort', s.objekt_ansprechpartner || k.ansprechpartner],
      ['Durchgeführt von', p.monteure || p.monteur_unterschrift_name],
    ]),
    subTitel('Angaben zur Wartung'),
    angabenZurWartung(p, config, msFelder, trafos),
  ));

  if (vnb) {
    content.push(zusammen(subTitel('Verteilnetzbetreiber (VNB)'), kvTable([
      ['Energieversorger', vnb.name], ['Telefon', vnb.telefon],
      ['Anschrift', anschrift(vnb.strasse, vnb.plz, vnb.ort)],
      ['Ansprechpartner', vnb.ansprechpartner],
      ['E-Mail', vnb.ansprechpartner_email || vnb.email],
    ])));
  }

  // 2 · Bewertung und Handlungsempfehlung
  content.push(zusammen(
    abschnitt(++nr, 'Bewertung und Handlungsempfehlung'),
    bewertungTabelle(p),
    empfehlungsBox(p, maengel),
    p.abschluss_bemerkung ? [subTitel('Bemerkung des Monteurs'), { text: p.abschluss_bemerkung, style: 'kWert', margin: [0, 0, 0, 6] }] : null,
  ));

  // 3 · Maengel
  const tabelle = maengelTabelle(maengel, module);
  content.push(abschnitt(++nr, 'Festgestellte Mängel'));
  content.push(tabelle || hinweisZeile('Es wurden keine Mängel festgestellt.'));

  // 4 · Die Anlage
  const feldSpez = (msFelder || []).map(f => MS_FELD_KURZ[f.typ_code] || f.typ_code).filter(Boolean).join(' – ');
  content.push(zusammen(abschnitt(++nr, 'Die Anlage'), kvTable([
    ['Bauform', STATIONSTYP_TEXT[c.stations_typ] || c.stations_typ],
    ['Hersteller Gebäude', c.stations_hersteller],
    ['Typ / Baujahr', [c.stations_modelltyp, c.stations_baujahr || s.baujahr].filter(hatWert).join(' · ')],
    ['Seriennummer', c.stations_seriennummer],
    ['MS-Anlage Hersteller', c.ms_anlage_hersteller],
    ['Isolierart', ISOLIERART_TEXT[c.ms_anlage_isolierart]],
    ['Anzahl Felder', c.ms_anlage_anzahl_felder],
    ['Betriebsspannung', fmtSpannungKv(c.ms_anlage_betriebsspannung_kv)],
    ['Spezifikation Felder', feldSpez],
    ['Verrechnungsmessung', VERRECHNUNG_TEXT[c.verrechnungsmessung]],
  ])));
  // Transformatoren, jeder mit seinen Typenschilddaten - je Trafo eine Einheit
  (trafos || []).forEach((t, i) => {
    content.push(zusammen(subTitel(t.bezeichnung || ('Transformator ' + (i + 1))), kvTable(trafoPaare(t, false)), schutzTabelle(t)));
  });

  // Alter der Betriebsmittel
  const alter = lebensdauerBefund(config, trafos, msFelder);
  if (alter.etwasGefunden) {
    content.push(zusammen(
      subTitel('Alter der Betriebsmittel'),
      datenTabelle([90, 110, '*'],
        [kopfZelle('Betriebsmittel'), kopfZelle('Baujahr / Alter'), kopfZelle('Einschätzung')],
        alter.posten.map(pos => [
          { text: pos.bezeichnung, style: 'tdZelle', bold: true },
          { text: pos.baujahr + ' · ' + pos.alter + ' Jahre', style: 'tdZelle' },
          { text: pos.stufe === 'ueberschritten'
              ? 'Die übliche Nutzungsdauer von ' + LEBENSDAUER_TEXT[pos.was] + ' Jahren ist überschritten.'
              : pos.stufe === 'bald'
                ? 'Die übliche Nutzungsdauer von ' + LEBENSDAUER_TEXT[pos.was] + ' Jahren wird im nächsten Jahr erreicht.'
                : 'Älter als 20 Jahre – wir empfehlen, mit der Zustandsbewertung und der Budgetplanung zu beginnen.',
            style: 'tdZelle', color: pos.stufe === 'ueberschritten' ? RED : pos.stufe === 'bald' ? ORANGE : NAVY },
        ])),
      { text: 'Richtwerte nach AMEV, VDI und HEA. Maßgeblich bleiben der tatsächliche Zustand, die Herstellerangaben '
        + 'und die Gefährdungsbeurteilung. Näheres in unserer Broschüre „Lebensdauer elektrischer Betriebsmittel".',
        style: 'hinweis', margin: [0, 0, 0, 6] },
    ));
  }

  // 5 · Messungen: Erderanlage und Thermografie
  const alleAnschluesse = (erder || []).flatMap(sch =>
    (sch.anschluesse || []).map(a => ({ ...a, schiene: sch.bezeichnung })));
  const thermoPp = (module || []).filter(m => m.modul_vorlagen?.code === '07-ELEKTROTHERMOGRAFIE')
    .flatMap(m => m.pruefpunkte || []).filter(istBeantwortet);
  // Die Ueberschrift wandert mit dem ersten Block - sonst bleibt sie allein
  // unten auf der Seite stehen, wenn der Block auf die naechste muss.
  let messKopf = (alleAnschluesse.length > 0 || thermoPp.length > 0) ? abschnitt(++nr, 'Messungen') : null;
  const mitKopf = (teile) => { const k = messKopf; messKopf = null; return k ? [k, ...teile] : teile; };

  if (alleAnschluesse.length > 0) {
    // "OL" (ueber_messbereich = true): das Messgeraet zeigte Ueberlauf, es gibt
    // keine messbare Verbindung, ohm_wert ist NULL. Zaehlt als gemessen UND als
    // ueber der Grenze - schlechter als jeder Zahlenwert.
    const istOL = (a) => a.messung?.ueber_messbereich === true;
    const istGemessen = (a) => istOL(a) || a.messung?.ohm_wert != null;
    const istUeberGrenze = (a) => istOL(a)
      || (a.messung?.ohm_wert != null && Number(a.messung.ohm_wert) > erderGrenzeVon(c));
    const gemessen = alleAnschluesse.filter(istGemessen);
    const ueberGrenze = gemessen.filter(istUeberGrenze);
    // Bild statt Tabelle: je Schiene Balken mit Abgaengen (erderGrafik).
    const bewertung = (a) => ({ gemessen: istGemessen(a), ol: istOL(a), ueber: istUeberGrenze(a) });
    const erderBild = (erder || []).filter(sch => (sch.anschluesse || []).length > 0).flatMap(sch => [
      { text: sch.bezeichnung || 'Erdungsschiene', bold: true, fontSize: 9, color: NAVY, margin: [0, 4, 0, 2] },
      ...erderGrafik(sch, bewertung),
    ]);
    const teile = mitKopf([
      subTitel('Erderanlage / Hauptpotentialausgleich'),
      { text: 'Erdungswiderstandsmessung – das Messergebnis muss ≤ ' + String(erderGrenzeVon(c)).replace('.', ',') + ' Ω betragen.'
        + (gemessen.some(istOL) ? ' OL = über dem Messbereich des Prüfgeräts, keine messbare Verbindung.' : ''),
        style: 'hinweis', margin: [0, 0, 0, 5] },
      ...erderBild,
    ]);
    if (ueberGrenze.length > 0) {
      teile.push({
        table: { widths: ['*'], body: [[{ stack: [
          { text: 'Bemerkung', bold: true, color: RED, margin: [0, 0, 0, 2] },
          { text: ERDER_BEMERKUNG_MANGEL, style: 'kWert', margin: [0, 0, 0, 6] },
          { text: 'Maßnahmen', bold: true, color: RED, margin: [0, 0, 0, 2] },
          { text: ERDER_MASSNAHME_MANGEL, style: 'kWert' },
        ] }]] },
        layout: { fillColor: () => REDBG, hLineWidth: () => 0, vLineWidth: (i) => (i === 0 ? 3 : 0), vLineColor: () => RED,
          paddingTop: () => 7, paddingBottom: () => 7, paddingLeft: () => 11, paddingRight: () => 10 },
        margin: [0, 0, 0, 8],
      });
    } else if (gemessen.length > 0) {
      teile.push({ text: 'Bemerkung: keine · Maßnahmen: keine erforderlich', style: 'kWert', color: GREEN, margin: [0, 0, 0, 8] });
    } else {
      // Vorher stand hier gruen "keine Massnahmen erforderlich", obwohl gar nicht gemessen wurde.
      teile.push({ text: 'In dieser Wartung wurde keine Erdungswiderstandsmessung durchgeführt.', style: 'kWert', color: GRAY, margin: [0, 0, 0, 8] });
    }
    content.push(alleAnschluesse.length <= 10 ? zusammen(teile) : { stack: teile });
  }

  if (thermoPp.length > 0) {
    content.push(zusammen(mitKopf([
      subTitel('Elektrothermografie'),
      datenTabelle([34, '*', 48, 160],
        [kopfZelle('Pos.'), kopfZelle('Bereich'), kopfZelle('Ergebnis', { alignment: 'center' }), kopfZelle('Hinweis')],
        thermoPp.map(pp => {
          const { ergebnis, hinweis } = ergebnisUndHinweis(pp);
          return [
            { text: pp.pos_nummer || '', style: 'tdZelleKlein' },
            { text: pp.bezeichnung || '', style: 'tdZelle', bold: pp.in_ordnung === false },
            ergebnisZelle(ergebnis, pp.in_ordnung),
            { text: hinweis, style: 'tdZelleKlein', color: pp.in_ordnung === false ? RED : GRAYDARK },
          ];
        })),
    ])));
  }

  // 6 · Fotodokumentation (Maengel-Fotos zuerst, siehe fotosAnhaengen)
  nr = fotosAnhaengen(content, daten.fotoBilder, module, nr);

  // 7 · Weiterfuehrende Informationen
  const allePp = [];
  (module || []).forEach(m => (m.pruefpunkte || []).forEach(
    pp => allePp.push({ ...pp, modul_code: m.modul_vorlagen?.code })));
  const broschueren = passendeBroschueren(allePp, c, alter);
  // Die Unterschrift geht zusammen mit dem letzten Abschnitt - sonst stand sie
  // oft allein auf einer eigenen letzten Seite.
  if (broschueren.length > 0) {
    content.push(zusammen(
      abschnitt(++nr, 'Weiterführende Informationen'),
      { text: 'Zu den festgestellten Punkten stellen wir Ihnen folgende Fachbroschüren bereit:', style: 'hinweis', margin: [0, 0, 0, 5] },
      { ul: broschueren.map(b => ({
          text: [{ text: b.titel, style: 'kWert', bold: true }, { text: '\n' + b.datei, fontSize: 7.5, color: GRAY, link: b.datei }],
          margin: [0, 0, 0, 4],
        })), margin: [0, 0, 0, 6] },
      unterschriftBlock(p),
    ));
  } else {
    content.push(unterschriftBlock(p));
  }

  return basisDocDef(content, artProtokoll(protokoll), protokoll, station);
}

// ---------------------------------------------------------------------------
// Daten laden
// ---------------------------------------------------------------------------
// Ladefehler duerfen NICHT still geschluckt werden: sonst sieht das PDF bei
// wackligem Netz vollstaendig aus, es fehlen aber ganze Kapitel ("keine Maengel",
// "Keine MS-Anlage", leere Pruefpunkte). Lieber abbrechen und nochmal versuchen.
// Eine fehlende optionale Zeile (maybeSingle ohne Treffer, data = null) ist KEIN Fehler.
function ladeFehler(teil, error) {
  const msg = error?.message || String(error || 'unbekannter Fehler');
  return new Error('PDF nicht erstellt: ' + teil + ' konnte nicht geladen werden ('
    + msg + '). Bitte bei besserem Netz nochmal versuchen.');
}
function datenOderFehler(res, teil) {
  if (res.error) throw ladeFehler(teil, res.error);
  return res.data;
}

// fuerKunde = true: Prüfpunkte, die das Büro vom Druck ausgenommen hat
// (nicht_drucken, Migration 77), fallen samt ihren Mängeln und Fotos weg.
// Die Mängelliste fürs Angebot laedt OHNE diese Einschraenkung und markiert
// die betroffenen Zeilen nur (Anna-Lena 14.10.2026).
export async function ladeProtokollDaten(protokollId, { fuerKunde = false } = {}) {
  const protokoll = datenOderFehler(await supabase.from('protokolle')
    .select('*, stationen(*, kunden(*))')
    .eq('id', protokollId).single(), 'Protokoll');

  const station = protokoll?.stationen || null;
  const kunde = station?.kunden || null;
  const stationId = protokoll?.station_id || station?.id;

  let config = null, msFelder = [], trafos = [], trafosAlle = [], wandler = [], vnb = null;
  if (stationId) {
    const [cRes, fRes, tRes, wRes] = await Promise.all([
      supabase.from('station_konfiguration').select('*').eq('station_id', stationId).maybeSingle(),
      // Nur aktive MS-Felder - geloeschte Felder sind nur weich geloescht (active = false).
      supabase.from('station_ms_felder').select('*').eq('station_id', stationId).eq('active', true).order('position'),
      // ALLE Trafos, auch inaktive: Module eines aelteren Protokolls koennen auf einen
      // inzwischen deaktivierten Trafo zeigen (trafosAlle, nur zum Nachschlagen per ID).
      // Die Stammdaten-Kapitel zeigen weiter nur die aktiven (trafos).
      supabase.from('station_trafos').select('*').eq('station_id', stationId).order('position'),
      supabase.from('station_wandler').select('*').eq('station_id', stationId).order('wandler_typ'),
    ]);
    config = datenOderFehler(cRes, 'Stationskonfiguration') || null;
    msFelder = datenOderFehler(fRes, 'MS-Feld-Liste') || [];
    trafosAlle = datenOderFehler(tRes, 'Trafo-Liste') || [];
    trafos = trafosAlle.filter(t => t.active !== false);
    wandler = (datenOderFehler(wRes, 'Wandler-Liste') || []).filter(w => w.active !== false);
  }
  if (station?.vnb_id) {
    vnb = datenOderFehler(await supabase.from('vnb').select('*').eq('id', station.vnb_id).maybeSingle(),
      'Energieversorger (VNB)') || null;
  }

  const modus = datenOderFehler(await supabase.from('protokoll_module')
    .select('*, modul_vorlagen(code, name, position, beschreibung)')
    .eq('protokoll_id', protokollId).order('reihenfolge'), 'Modul-Liste') || [];

  // Alle Pruefpunkte in EINER Anfrage (frueher eine je Modul) und hier nach Modul
  // sortieren. Pruefpunkte haengen nur ueber protokoll_modul_id am Protokoll -
  // eine Spalte protokoll_id hat protokoll_modul_pruefpunkte NICHT.
  const ppNachModul = {};
  const modulIds = modus.map(m => m.id);
  if (modulIds.length) {
    const pps = datenOderFehler(await supabase.from('protokoll_modul_pruefpunkte')
      .select('*').in('protokoll_modul_id', modulIds).order('reihenfolge'), 'Prüfpunkt-Liste') || [];
    pps.forEach((pp) => {
      (ppNachModul[pp.protokoll_modul_id] = ppNachModul[pp.protokoll_modul_id] || []).push(pp);
    });
  }
  // Nur Pruefpunkte, die unter den gegebenen Antworten ueberhaupt gelten
  // (gleiche Regel wie im Protokoll). Ausgeblendete Folgefragen - und ihre
  // Maengel - gehoeren nicht ins PDF: vorher standen z. B. alte n.i.O. einer
  // Blechwanne im Bericht, obwohl "Ölwanne vorhanden: nein" beantwortet war.
  // Fuer die Sichtbarkeit zaehlen auch Trafos, auf die ein Kapitel DIESES
  // Protokolls zeigt, selbst wenn sie inzwischen stillgelegt sind - sonst
  // aenderte sich das PDF einer alten Wartung spaeter noch.
  const bezugIds = new Set(modus.map(m => m.bezugs_id).filter(Boolean));
  const trafosFuerSicht = [...trafos,
    ...(trafosAlle || []).filter(t => t.active === false && bezugIds.has(t.id)).map(t => ({ ...t, active: true }))];
  const lageFuerSicht = config ? anlagenLage(config, msFelder, trafos) : undefined;
  const module = modus.map(m => {
    const alle = ppNachModul[m.id] || [];
    const kapitelTrafo = m.bezugs_id ? trafosFuerSicht.find(t => t.id === m.bezugs_id) : undefined;
    const sichtbar = alle.filter(pp => istSichtbar(pp, alle, trafosFuerSicht,
      { config, vnbName: station?.vnb_id ? (vnb?.name ?? undefined) : null, lage: lageFuerSicht, trafo: kapitelTrafo }));
    // Nummer wie in der App (Kapitel 02 und 04 fortlaufend); pos_nummer bleibt intern.
    const nummern = anzeigeNummern(m.modul_vorlagen?.code, sichtbar);
    // Die Nummerierung entsteht VOR dem Druckfilter - sonst aenderten sich die
    // Nummern im Kundendokument, sobald das Buero einen Punkt herausnimmt.
    const gedruckt = fuerKunde ? sichtbar.filter(pp => !pp.nicht_drucken) : sichtbar;
    return { ...m, pruefpunkte: gedruckt.map(pp => ({ ...pp, anzeige_nummer: nummern[pp.id] || pp.pos_nummer })) };
  });
  // Enthaelt im Kundendokument nur noch die wirklich gedruckten Punkte.
  const sichtbareIds = new Set(module.flatMap(m => m.pruefpunkte.map(pp => pp.id)));
  const alleIds = new Set(Object.values(ppNachModul).flat().map(pp => pp.id));

  const maengel = (datenOderFehler(await supabase.from('maengel')
    .select('*').eq('protokoll_id', protokollId).order('created_at'), 'Mängelliste') || [])
    .filter(mg => !mg.pruefpunkt_id || !alleIds.has(mg.pruefpunkt_id) || sichtbareIds.has(mg.pruefpunkt_id));

  // Erderanlage: Schienen und Anschluesse gehoeren zur Station, die Ohm-Werte
  // zu dieser Wartung. Fuer den Kundenbericht brauchen wir beides zusammen.
  let erder = [];
  if (stationId) {
    const schienen = datenOderFehler(await supabase.from('station_erderanlage_schienen')
      .select('*').eq('station_id', stationId).eq('active', true).order('reihenfolge'),
      'Erderanlage (Schienen)') || [];
    const ids = schienen.map(x => x.id);
    const messungen = datenOderFehler(await supabase.from('protokoll_erderanlage_messungen')
      .select('*').eq('protokoll_id', protokollId), 'Erderanlage (Messwerte)') || [];
    const nachId = {};
    (messungen || []).forEach(m => { nachId[m.anschluss_id] = m; });
    let anschluesse = [];
    if (ids.length) {
      // Auch stillgelegte Anschluesse, sofern in DIESER Wartung gemessen -
      // sonst verschwand der Messwert aus spaeter erstellten PDFs.
      anschluesse = (datenOderFehler(await supabase.from('station_erderanlage_anschluesse')
        .select('*').in('schiene_id', ids).order('reihenfolge'),
        'Erderanlage (Anschlüsse)') || []).filter(a => a.active !== false || nachId[a.id]);
    }
    erder = (schienen || []).map(s => ({
      ...s,
      anschluesse: anschluesse.filter(a => a.schiene_id === s.id)
        .map(a => ({ ...a, messung: nachId[a.id] || null })),
    }));
  }

  // 06.3: Der gespeicherte Auto-Text kann veraltet sein (Monteur tippte 99999, das
  // Buero stellte spaeter auf OL - Anna-Lena 21.09.2026). Im PDF wird er deshalb
  // immer aus den Messwerten DIESER Wartung neu gebildet. Eigene Bemerkungen des
  // Monteurs (ohne "Auto-Bewertung:") bleiben unangetastet.
  const befund = erderBefund(erder, erderGrenzeVon(config));
  if (befund && befund.anzahlUeberGrenze > 0) {
    const text = 'Auto-Bewertung: ' + erderAutoText(befund);
    module.forEach(m => (m.pruefpunkte || []).forEach(pp => {
      if (String(pp.pos_nummer || '').trim() !== '06.3') return;
      if (!String(pp.bemerkung || '').startsWith('Auto-Bewertung:')) return;
      pp.bemerkung = text;
      (maengel || []).filter(x => x.pruefpunkt_id === pp.id).forEach(x => {
        x.beschreibung = /Auto-Bewertung:/.test(String(x.beschreibung || ''))
          ? String(x.beschreibung).replace(/Auto-Bewertung:.*$/, text) : x.beschreibung;
      });
    }));
  }

  // Fotos, die fuer den Ausdruck freigegeben sind. Im Kundendokument ohne die
  // Fotos der ausgenommenen Pruefpunkte.
  const fotos = (datenOderFehler(await supabase.from('protokoll_fotos')
    .select('*').eq('protokoll_id', protokollId).eq('in_pdf', true).order('erstellt_am'), 'Foto-Liste') || [])
    .filter(f => !fuerKunde || !f.pruefpunkt_id || sichtbareIds.has(f.pruefpunkt_id));

  // Fuer die Maengelliste: welche Punkte stehen NICHT im Kundendokument?
  const nichtGedruckt = new Set(Object.values(ppNachModul).flat()
    .filter(pp => pp.nicht_drucken).map(pp => pp.id));

  return { protokoll, kunde, vnb, station, config, msFelder, trafos, trafosAlle, wandler,
           module, maengel, erder, fotos, nichtGedruckt };
}

// ---------------------------------------------------------------------------
// pdfmake laden (lazy) + Download
// ---------------------------------------------------------------------------
let _pdfMakePromise = null;
async function getPdfMake({ vorab = false } = {}) {
  if (!_pdfMakePromise) {
    _pdfMakePromise = (async () => {
      const pdfMakeMod = await import('pdfmake/build/pdfmake');
      const pdfFontsMod = await import('pdfmake/build/vfs_fonts');
      const pdfMake = pdfMakeMod.default || pdfMakeMod;
      const pdfFonts = pdfFontsMod.default || pdfFontsMod;
      pdfMake.vfs = (pdfFonts.pdfMake && pdfFonts.pdfMake.vfs) || pdfFonts.vfs;
      return pdfMake;
    })().catch((e) => {
      // Fehlschlag NICHT merken - sonst blieb das PDF bis zum Neustart kaputt.
      // Fehlt der Baustein nach einem Update auf dem Server, neu laden.
      _pdfMakePromise = null;
      // Nur bei einem echten Download und mit Netz - ohne Netz fehlt der
      // Baustein einfach, und das Vorabladen beim Oeffnen des Abschluss-Reiters
      // hat den Monteur sonst auf die Startseite geworfen.
      if (!vorab && navigator.onLine && istChunkFehler(e)) nachUpdateNeuLaden();
      throw e;
    });
  }
  return _pdfMakePromise;
}
export function prefetchPdfEngine() { getPdfMake({ vorab: true }).catch(() => {}); }

function dateiname(protokoll, station, typ) {
  const teile = [typ];
  const stName = station?.bezeichnung || protokoll?.stationsbezeich;
  if (stName) teile.push(String(stName).replace(/[^\w-]+/g, '_'));
  if (protokoll?.wartungs_datum) teile.push(String(protokoll.wartungs_datum).slice(0, 10));
  return teile.join('_') + '.pdf';
}
export async function downloadVollprotokoll(protokollId) {
  const daten = await ladeProtokollDaten(protokollId, { fuerKunde: true });
  daten.fotoBilder = await ladeFotoBilder(daten.fotos);
  const pdfMake = await getPdfMake();
  pdfMake.createPdf(buildVollprotokollDocDef(daten)).download(dateiname(daten.protokoll, daten.station, istSicht(daten.protokoll) ? 'Sichtpruefungsprotokoll' : 'Wartungsprotokoll'));
}
// Fuer das Monats-Archiv (Verwaltung): dasselbe Vollprotokoll wie beim
// Download, aber als Datei-Inhalt statt Download.
export async function vollprotokollAlsBlob(protokollId) {
  const daten = await ladeProtokollDaten(protokollId, { fuerKunde: true });
  daten.fotoBilder = await ladeFotoBilder(daten.fotos);
  const pdfMake = await getPdfMake();
  const blob = await new Promise((fertig) => pdfMake.createPdf(buildVollprotokollDocDef(daten)).getBlob(fertig));
  return { blob, daten };
}
export async function downloadMaengelListe(protokollId) {
  const daten = await ladeProtokollDaten(protokollId);
  const pdfMake = await getPdfMake();
  pdfMake.createPdf(buildMaengelDocDef(daten)).download(dateiname(daten.protokoll, daten.station, 'Maengelliste'));
}
// Bilder muessen als data:-URL im PDF landen; eine http-Adresse kann pdfmake
// nicht laden. Schlaegt ein Bild fehl (kein Netz, geloescht), wird es einfach
// weggelassen - das PDF entsteht trotzdem.
// Ein normales fetch() hat KEINE Zeitgrenze (die zentrale in lib/supabase.js gilt
// nur fuer den Supabase-Client). Ohne eigene Grenze blieb "Erstelle PDF ..." bei
// schlechtem Netz ewig stehen. Jetzt: je Bild hoechstens 30 s, dann uebersprungen.
const ZEITGRENZE_FOTO_MS = 30000;
async function ladeFotoBilder(fotos) {
  const ergebnis = [];
  for (const f of (fotos || [])) {
    if (!f.public_url) continue;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), ZEITGRENZE_FOTO_MS);
    try {
      // Das Signal bricht auch das Lesen des Inhalts (blob) ab, nicht nur den Verbindungsaufbau.
      const antwort = await fetch(f.public_url, { signal: ctrl.signal });
      if (!antwort.ok) continue;
      const blob = await antwort.blob();
      // Der oeffentliche Speicher liefert im Fehlerfall HTML mit Status 200.
      // Landet das als "Bild" im Dokument, bricht pdfmake OHNE Fehler ab und
      // der Download bleibt ewig haengen (Pruefung 20.09.2026).
      if (!/^image\//.test(blob.type || '')) continue;
      const datenUrl = await new Promise((auf, ab) => {
        const leser = new FileReader();
        leser.onload = () => auf(leser.result);
        leser.onerror = ab;
        leser.readAsDataURL(blob);
      });
      ergebnis.push({ datenUrl, titel: f.titel || f.dateiname || '', pruefpunkt_id: f.pruefpunkt_id || null });
    } catch {
      // Ein fehlendes oder zu langsames Bild darf den ganzen Export nicht aufhalten.
    } finally {
      clearTimeout(timer);
    }
  }
  return ergebnis;
}

export async function downloadKurzbericht(protokollId) {
  const daten = await ladeProtokollDaten(protokollId, { fuerKunde: true });
  daten.fotoBilder = await ladeFotoBilder(daten.fotos);
  const pdfMake = await getPdfMake();
  pdfMake.createPdf(buildKurzberichtDocDef(daten)).download(dateiname(daten.protokoll, daten.station, istSicht(daten.protokoll) ? 'Sichtpruefungsbericht' : 'Wartungsbericht'));
}
